import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDDMMYYDisplay, parseDDMMYY, compareByMonthYear } from "@/utils/dateUtils";
import {
  TrendingUp,
  Upload,
  RefreshCw,
  Search,
  FileSpreadsheet,
  IndianRupee,
  Calculator,
  Package,
  Users,
  ChevronRight,
  ChevronDown,
  Trash2,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  XCircle,
  Wallet,
  Target,
  Calendar,
  FileText,
  Zap,
  ArrowLeft,
  UserCheck,
  Handshake,
  LayoutGrid,
  PieChart,
  DollarSign,
  BookOpen,
  AlertTriangle,
  AlertCircle,
  Check,
  X,
  Plus,
  History,
  Clock,
  Table,
  ChevronLeft,
  MoreVertical,
  ExternalLink,
  ChevronsUpDown,
  Download,
  FileDown,
  Link2,
  Info
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import { formatDMY } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SalesPage = () => {
  usePageTitle("Sales & Profit");
  const { token } = useAuth();
  
  // Data states
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadTime, setLoadTime] = useState(null); // Track API response time in ms
  const [activeTab, setActiveTab] = useState("overview");
  
  // Client drill-down states
  const [selectedClient, setSelectedClient] = useState(null); // Currently selected client code
  const [clientViewLevel, setClientViewLevel] = useState("list"); // list, year, month, date
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [partnerDrilldownSales, setPartnerDrilldownSales] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  
  // Delete entries from drill-down states
  const [showDeleteDrilldownDialog, setShowDeleteDrilldownDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletingDrilldownEntries, setDeletingDrilldownEntries] = useState(false);
  const [entriesToDelete, setEntriesToDelete] = useState([]); // [{tracking, sell_price, order_date}]
  const [showDeletedEntriesDialog, setShowDeletedEntriesDialog] = useState(false);
  const [deletedEntries, setDeletedEntries] = useState([]);
  const [restoringEntries, setRestoringEntries] = useState(false);
  const [restorePassword, setRestorePassword] = useState("");
  
  // Calculated sales preview
  const [calculatedSales, setCalculatedSales] = useState([]);
  const [calculatedSummary, setCalculatedSummary] = useState({});
  const [calculating, setCalculating] = useState(false);
  
  // Bulk import state for DP Client products
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [bulkImportData, setBulkImportData] = useState([]);
  const [importingProducts, setImportingProducts] = useState(false);
  
  // Filters & Sort
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "total_profit", direction: "desc" });
  const [filterStatus, setFilterStatus] = useState("all"); // "all", "matched", "unmatched"
  
  // Advanced Search states
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedSearchFilters, setAdvancedSearchFilters] = useState({
    tracking: "",
    customerName: "",
    productName: "",
    dateFrom: "",
    dateTo: "",
    globalSearch: ""
  });
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultsSummary, setSearchResultsSummary] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResultsPage, setSearchResultsPage] = useState(1);
  const [searchResultsTotal, setSearchResultsTotal] = useState(0);
  const SEARCH_PAGE_SIZE = 50;
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);
  
  // Rematch state
  const [isRematching, setIsRematching] = useState(false);
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showCalculateDialog, setShowCalculateDialog] = useState(false);
  const [showPartnerUploadDialog, setShowPartnerUploadDialog] = useState(false);
  const [showAgentUploadDialog, setShowAgentUploadDialog] = useState(false);
  const [generatingMissing, setGeneratingMissing] = useState(false);
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  const [resettingPartnerSellPrices, setResettingPartnerSellPrices] = useState(false);
  const [showRawPartnerUploadDialog, setShowRawPartnerUploadDialog] = useState(false);
  const [selectedPartnerForUpload, setSelectedPartnerForUpload] = useState(null);
  const [selectedAgentForUpload, setSelectedAgentForUpload] = useState(null);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [regeneratingSales, setRegeneratingSales] = useState(false);
  
  // DP Client Sales generation state
  const [generatingDpSales, setGeneratingDpSales] = useState(false);
  const [dpSalesYear, setDpSalesYear] = useState(String(new Date().getFullYear()));
  const [dpSalesResult, setDpSalesResult] = useState(null);
  const [showDpSalesResultDialog, setShowDpSalesResultDialog] = useState(false);
  
  // Summary by role
  const [allClientsSummary, setAllClientsSummary] = useState([]);
  const [clientSummary, setClientSummary] = useState([]);
  const [partnerSummary, setPartnerSummary] = useState([]);
  const [agentSummary, setAgentSummary] = useState([]);
  
  // Ledger data for role lookup
  const [ledgers, setLedgers] = useState([]);
  
  // Upload History states
  const [uploadHistory, setUploadHistory] = useState([]);
  const [uploadHistoryLoading, setUploadHistoryLoading] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [uploadRawData, setUploadRawData] = useState([]);
  const [uploadRawDataLoading, setUploadRawDataLoading] = useState(false);
  const [showUploadRawDataDialog, setShowUploadRawDataDialog] = useState(false);
  const [uploadRawDataTotal, setUploadRawDataTotal] = useState(0);
  const [rawDataSortField, setRawDataSortField] = useState(null);
  const [rawDataSortOrder, setRawDataSortOrder] = useState('asc');
  const [rawDataPage, setRawDataPage] = useState(1);
  const [rawDataPageSize] = useState(100);
  const [rawDataSearch, setRawDataSearch] = useState('');

  // Currency/crypto keywords to detect for narration
  const CURRENCY_KEYWORDS = [
    // Major fiat currencies
    'usd', 'dollar', '$', '€', 'euro', 'eur', '£', 'gbp', 'pound', 'sterling',
    '¥', 'yen', 'jpy', 'cny', 'yuan', 'rmb', 'renminbi',
    'aud', 'cad', 'chf', 'franc', 'nzd', 'sgd', 'hkd', 'krw', 'won',
    'myr', 'ringgit', 'thb', 'baht', 'php', 'peso', 'idr', 'rupiah',
    'vnd', 'dong', 'brl', 'real', 'mxn', 'zar', 'rand',
    // Middle East currencies
    'aed', 'dirham', 'sar', 'riyal', 'qar', 'kwd', 'dinar', 'bhd', 'omr',
    // Crypto
    'btc', 'bitcoin', 'usdt', 'tether', 'ethereum', 'eth', 'tron', 'trx',
    'bnb', 'binance', 'xrp', 'ripple', 'sol', 'solana', 'ada', 'cardano',
    'doge', 'dogecoin', 'ltc', 'litecoin', 'usdc', 'busd', 'crypto',
    // Special keyword - Crocin maps to ESR@
    'crocin'
  ];

  // Extract narration from row - checks raw_data for currency keywords and values
  const extractNarration = (row) => {
    // Use raw_data if available, otherwise use all row fields
    const rawData = row.raw_data || row;
    const allData = { ...row, ...rawData };
    
    // Combine all string fields to search for currency keywords
    const searchText = Object.values(allData)
      .filter(v => typeof v === 'string')
      .join(' ')
      .toLowerCase();
    
    // Find matching currency keywords
    const foundCurrencies = CURRENCY_KEYWORDS.filter(keyword => 
      searchText.includes(keyword.toLowerCase())
    );
    
    if (foundCurrencies.length === 0) {
      return { hasNarration: false };
    }
    
    // Build narration parts
    const narrationParts = [];
    let hasCrocin = false;
    
    // Check for special "crocin" keyword
    if (searchText.includes('crocin')) {
      hasCrocin = true;
    }
    
    // Look for $ values in shipping and total cost columns
    const keys = Object.keys(allData);
    
    // Find shipping value (look for columns with 'shipping' or 'ship' in the name)
    const shippingKey = keys.find(k => k.toLowerCase().includes('shipping') || k.toLowerCase().includes('ship'));
    if (shippingKey && allData[shippingKey]) {
      const shippingVal = String(allData[shippingKey]).trim();
      if (shippingVal && shippingVal !== '0') {
        // Check if it has $ or extract the number
        if (shippingVal.includes('$') || /^\d+\.?\d*$/.test(shippingVal)) {
          const numVal = shippingVal.replace(/[^0-9.]/g, '');
          narrationParts.push(`$${numVal} shipping`);
        }
      }
    }
    
    // Find total cost value
    const totalCostKey = keys.find(k => 
      (k.toLowerCase().includes('total') && k.toLowerCase().includes('cost')) ||
      k.toLowerCase() === 'total cost'
    );
    if (totalCostKey && allData[totalCostKey]) {
      const costVal = String(allData[totalCostKey]).trim();
      if (costVal && costVal !== '0') {
        const numVal = costVal.replace(/[^0-9.]/g, '');
        if (numVal) {
          narrationParts.push(`$${numVal} total cost`);
        }
      }
    }
    
    // Get INR/Payment value for display
    let inrAmount = 0;
    const inrKey = keys.find(k => k.toLowerCase() === 'inr' || k.toLowerCase().includes('inr'));
    const paymentKey = keys.find(k => k.toLowerCase().includes('payment') || k.toLowerCase().includes('pay'));
    
    if (inrKey && allData[inrKey]) {
      inrAmount = parseFloat(String(allData[inrKey]).replace(/[^0-9.]/g, '')) || 0;
    } else if (paymentKey && allData[paymentKey]) {
      inrAmount = parseFloat(String(allData[paymentKey]).replace(/[^0-9.]/g, '')) || 0;
    } else {
      inrAmount = row.sell_price || row.amount || 0;
    }
    
    // Determine primary currency indicator
    let currencyFound = foundCurrencies[0].toUpperCase();
    if (currencyFound === 'CROCIN') {
      currencyFound = 'ESR@';
    }
    
    // Build display string
    let display = '';
    if (narrationParts.length > 0) {
      display = narrationParts.join(', ');
      if (hasCrocin) {
        display += ' | ESR@';
      }
    } else if (hasCrocin) {
      display = `ESR@ → ₹${inrAmount.toLocaleString('en-IN')}`;
    } else {
      display = `${currencyFound} → ₹${inrAmount.toLocaleString('en-IN')}`;
    }
    
    return {
      hasNarration: true,
      currency: hasCrocin ? 'ESR@' : currencyFound,
      inrAmount: inrAmount,
      narrationParts: narrationParts,
      hasCrocin: hasCrocin,
      display: display
    };
  };

  // Data is now sorted and filtered server-side, so just use uploadRawData directly
  // This memo is kept for any additional client-side processing if needed
  const displayRawData = useMemo(() => {
    return uploadRawData;
  }, [uploadRawData]);

  // Open raw data in full screen new tab
  const openRawDataFullScreen = () => {
    if (!selectedUpload) return;
    // Store data in localStorage for the new tab (sessionStorage is per-tab)
    localStorage.setItem('salesRawData', JSON.stringify({
      upload: selectedUpload,
      data: uploadRawData,
      total: uploadRawDataTotal
    }));
    window.open('/sales/raw-data-view', '_blank');
  };
  
  // Helper function to download template with authentication
  const downloadTemplate = async (templateType) => {
    try {
      const endpoint = templateType === 'partner' 
        ? `${BACKEND_URL}/api/sales/partner-template`
        : templateType === 'agent'
          ? `${BACKEND_URL}/api/sales/agent-template`
          : `${BACKEND_URL}/api/sales/client-template`;
      
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download template');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateType}_sales_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Template download error:', error);
      toast.error('Failed to download template');
    }
  };
  
  // GP Distribution states
  const [gpData, setGpData] = useState(null);
  const [gpLoading, setGpLoading] = useState(false);
  const [gpSelectedCode, setGpSelectedCode] = useState(null);
  const [gpCodeData, setGpCodeData] = useState(null);
  const [gpViewLevel, setGpViewLevel] = useState("summary"); // summary, code, year, month, date
  const [gpSelectedYear, setGpSelectedYear] = useState(null);
  const [gpSelectedMonth, setGpSelectedMonth] = useState(null);
  const [gpSearchTerm, setGpSearchTerm] = useState("");
  const [gpSortConfig, setGpSortConfig] = useState({ key: "total_profit", direction: "desc" });
  const [gpRoleFilter, setGpRoleFilter] = useState("all"); // all, Client, Partner, Agent

  // Ledger Reconciliation states
  const [reconciliationData, setReconciliationData] = useState(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [reconciliationSelectedCode, setReconciliationSelectedCode] = useState(null);
  const [reconciliationDetails, setReconciliationDetails] = useState(null);
  const [reconciliationViewLevel, setReconciliationViewLevel] = useState("summary"); // summary, details, tracking
  const [reconciliationSearch, setReconciliationSearch] = useState("");
  const [reconciliationDateFrom, setReconciliationDateFrom] = useState("");
  const [reconciliationDateTo, setReconciliationDateTo] = useState("");
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  const [manualEntryType, setManualEntryType] = useState("take"); // take or give
  const [manualEntryAmount, setManualEntryAmount] = useState("");
  const [manualEntryDate, setManualEntryDate] = useState("");
  const [manualEntryNarration, setManualEntryNarration] = useState("");
  const [manualEntryTracking, setManualEntryTracking] = useState("");
  const [pendingApprovals, setPendingApprovals] = useState([]);

  // Fetch Ledger Reconciliation data
  const fetchReconciliationData = useCallback(async () => {
    setReconciliationLoading(true);
    try {
      let url = `${BACKEND_URL}/api/ledger-reconciliation/summary`;
      const params = new URLSearchParams();
      if (reconciliationDateFrom) params.append("date_from", reconciliationDateFrom);
      if (reconciliationDateTo) params.append("date_to", reconciliationDateTo);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setReconciliationData(data);
      }
    } catch (error) {
      console.error("Error fetching reconciliation data:", error);
      toast.error("Failed to fetch ledger reconciliation data");
    } finally {
      setReconciliationLoading(false);
    }
  }, [token, reconciliationDateFrom, reconciliationDateTo]);

  // Fetch details for a specific client code
  const fetchReconciliationDetails = useCallback(async (clientCode) => {
    setReconciliationLoading(true);
    try {
      let url = `${BACKEND_URL}/api/ledger-reconciliation/${clientCode}/details`;
      const params = new URLSearchParams();
      if (reconciliationDateFrom) params.append("date_from", reconciliationDateFrom);
      if (reconciliationDateTo) params.append("date_to", reconciliationDateTo);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setReconciliationDetails(data);
        setReconciliationSelectedCode(clientCode);
        setReconciliationViewLevel("details");
      }
    } catch (error) {
      console.error("Error fetching reconciliation details:", error);
      toast.error("Failed to fetch client details");
    } finally {
      setReconciliationLoading(false);
    }
  }, [token, reconciliationDateFrom, reconciliationDateTo]);

  // Create manual entry
  const handleCreateManualEntry = async () => {
    if (!reconciliationSelectedCode || !manualEntryAmount || !manualEntryDate || !manualEntryNarration) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-reconciliation/manual-entry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          client_code: reconciliationSelectedCode,
          entry_type: manualEntryType,
          amount: parseFloat(manualEntryAmount),
          date: manualEntryDate,
          narration: manualEntryNarration,
          tracking_number: manualEntryTracking || null
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowManualEntryDialog(false);
        setManualEntryAmount("");
        setManualEntryDate("");
        setManualEntryNarration("");
        setManualEntryTracking("");
        // Refresh data
        fetchReconciliationDetails(reconciliationSelectedCode);
      } else {
        toast.error(data.detail || "Failed to create entry");
      }
    } catch (error) {
      console.error("Error creating manual entry:", error);
      toast.error("Failed to create manual entry");
    }
  };

  // Fetch pending approvals (admin only)
  const fetchPendingApprovals = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-reconciliation/pending-approvals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingApprovals(data.entries || []);
      }
    } catch (error) {
      console.error("Error fetching pending approvals:", error);
    }
  }, [token]);

  // Approve/reject manual entry
  const handleApproveEntry = async (entryId, approved) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-reconciliation/manual-entry/${entryId}/approve`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ approved, remarks: "" })
      });
      
      if (response.ok) {
        toast.success(approved ? "Entry approved" : "Entry rejected");
        fetchPendingApprovals();
        if (reconciliationSelectedCode) {
          fetchReconciliationDetails(reconciliationSelectedCode);
        }
      }
    } catch (error) {
      console.error("Error approving entry:", error);
      toast.error("Failed to process approval");
    }
  };

  // Fetch ledgers for role information
  const fetchLedgers = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers-table`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setLedgers(data.ledgers || []);
    } catch (error) {
      console.error("Error fetching ledgers:", error);
    }
  }, [token]);

  // Fetch GP distribution data
  const fetchGpData = useCallback(async () => {
    setGpLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/gp-distribution/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setGpData(data);
      }
    } catch (error) {
      console.error("Error fetching GP data:", error);
    } finally {
      setGpLoading(false);
    }
  }, [token]);

  // Fetch GP data for specific code
  const fetchGpCodeData = useCallback(async (code, year = null, month = null) => {
    try {
      let url = `${BACKEND_URL}/api/gp-distribution/by-code/${code}`;
      const params = new URLSearchParams();
      if (year) params.append("year", year);
      if (month) params.append("month", month);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setGpCodeData(data);
      }
    } catch (error) {
      console.error("Error fetching GP code data:", error);
    }
  }, [token]);

  // Fetch sales data
  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const startTime = performance.now(); // Start timing
      const [salesResp, summaryResp] = await Promise.all([
        fetch(`${BACKEND_URL}/api/sales`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/sales/all-partners-summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      const endTime = performance.now(); // End timing
      setLoadTime(Math.round(endTime - startTime)); // Store load time in ms
      const data = await salesResp.json();
      setSales(data.sales || []);
      setSummary(data.summary || {});
      
      // Use backend-calculated partner summaries (accurate, deduped, matches Ledger)
      if (summaryResp.ok) {
        const summData = await summaryResp.json();
        const partners = summData.partners || [];
        setAllClientsSummary(partners);
        setPartnerSummary(partners.filter(p => p.role === "Partner"));
        setAgentSummary(partners.filter(p => p.role === "Agent"));
        setClientSummary(partners.filter(p => !["Partner", "Agent"].includes(p.role)));
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
      toast.error("Failed to load sales data");
      setLoadTime(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Advanced search function
  const performAdvancedSearch = useCallback(async (page = 1) => {
    const { tracking, customerName, productName, dateFrom, dateTo, globalSearch } = advancedSearchFilters;
    
    // Check if any filter is active
    const hasFilters = tracking || customerName || productName || dateFrom || dateTo || globalSearch;
    if (!hasFilters) {
      toast.info("Please enter at least one search criteria");
      return;
    }
    
    setIsSearching(true);
    setSearchResultsPage(page);
    
    try {
      const params = new URLSearchParams();
      if (tracking) params.append("tracking", tracking);
      if (customerName) params.append("customer_name", customerName);
      if (productName) params.append("product_name", productName);
      if (dateFrom) params.append("start_date", dateFrom);
      if (dateTo) params.append("end_date", dateTo);
      if (globalSearch) params.append("search", globalSearch);
      params.append("limit", SEARCH_PAGE_SIZE);
      params.append("skip", (page - 1) * SEARCH_PAGE_SIZE);
      
      const response = await fetch(`${BACKEND_URL}/api/sales?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (response.ok) {
        setSearchResults(data.sales || []);
        setSearchResultsSummary(data.summary || null);
        setSearchResultsTotal(data.total_count || data.sales?.length || 0);
        setShowSearchResults(true);
        
        if (data.sales?.length === 0) {
          toast.info("No matching records found");
        } else {
          toast.success(`Found ${data.total_count || data.sales?.length} matching records`);
        }
      } else {
        toast.error(data.detail || "Search failed");
      }
    } catch (error) {
      console.error("Error performing search:", error);
      toast.error("Failed to perform search");
    } finally {
      setIsSearching(false);
    }
  }, [token, advancedSearchFilters, SEARCH_PAGE_SIZE]);
  
  // Clear search and go back to summary view
  const clearAdvancedSearch = () => {
    setAdvancedSearchFilters({
      tracking: "",
      customerName: "",
      productName: "",
      dateFrom: "",
      dateTo: "",
      globalSearch: ""
    });
    setSearchResults([]);
    setSearchResultsSummary(null);
    setShowSearchResults(false);
    setSearchResultsPage(1);
    setSearchResultsTotal(0);
  };

  // State for sync progress
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, isRunning: false });

  // Generate missing sales records for orders without sales - with auto batch processing
  const generateMissingSales = async () => {
    if (!window.confirm("This will create placeholder sales records for all orders that don't have sales entries. This may take a few minutes for large datasets. Continue?")) {
      return;
    }
    
    setGeneratingMissing(true);
    setSyncProgress({ current: 0, total: 0, isRunning: true });
    
    let totalCreated = 0;
    let skip = 0;
    const batchSize = 5000;
    let remaining = 1; // Start with 1 to enter loop
    
    try {
      while (remaining > 0) {
        const response = await fetch(`${BACKEND_URL}/api/sales/generate-missing`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ batch_size: batchSize, skip: skip })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          toast.error(data.detail || "Failed to generate missing sales");
          break;
        }
        
        totalCreated += data.created || 0;
        remaining = data.remaining || 0;
        
        // Update progress
        const totalMissing = data.total_missing || 0;
        setSyncProgress({ 
          current: totalMissing - remaining, 
          total: totalMissing,
          isRunning: remaining > 0
        });
        
        if (data.created > 0) {
          toast.info(`Batch synced: ${data.created} records (${remaining} remaining)`);
        }
        
        skip += batchSize;
        
        // Small delay to prevent overwhelming the server
        if (remaining > 0) {
          await new Promise(r => setTimeout(r, 500));
        }
      }
      
      if (totalCreated > 0) {
        toast.success(`✓ Sync complete! Created ${totalCreated} placeholder sales records`);
        fetchSales(); // Refresh data
      } else {
        toast.info("All orders already have sales records");
      }
    } catch (error) {
      console.error("Error generating missing sales:", error);
      toast.error("Failed to generate missing sales");
    } finally {
      setGeneratingMissing(false);
      setSyncProgress({ current: 0, total: 0, isRunning: false });
    }
  };

  // Cleanup duplicate placeholder sales records
  const cleanupDuplicateSales = async () => {
    setCleaningDuplicates(true);
    let totalDeleted = 0;
    let hasMore = true;
    
    try {
      // Process in batches until no more duplicates
      while (hasMore) {
        const response = await fetch(`${BACKEND_URL}/api/sales/cleanup-duplicates`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ batch_size: 500 })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          toast.error(data.detail || "Failed to cleanup duplicates");
          break;
        }
        
        totalDeleted += data.deleted || 0;
        hasMore = data.has_more || false;
        
        if (data.deleted > 0) {
          toast.info(`Cleaned ${totalDeleted} duplicates so far...`);
        }
        
        // If no more to process, break
        if (!hasMore || data.deleted === 0) {
          break;
        }
      }
      
      if (totalDeleted > 0) {
        toast.success(`Total cleaned: ${totalDeleted} duplicate placeholder records`);
        // Refresh the data
        fetchSales();
      } else {
        toast.info("No duplicate placeholders found to clean up");
      }
    } catch (error) {
      toast.error("Failed to cleanup duplicates");
    } finally {
      setCleaningDuplicates(false);
    }
  };

  // Reset incorrect sell prices for Partner/Agent records
  const resetPartnerAgentSellPrices = async () => {
    if (!window.confirm(
      "This will reset sell_price to 0 for all Partner/Agent records where sales were incorrectly auto-calculated.\n\n" +
      "DP Client records will NOT be affected.\n\nProceed?"
    )) return;
    
    setResettingPartnerSellPrices(true);
    try {
      // First dry run to show count
      const dryRes = await fetch(`${BACKEND_URL}/api/sales/reset-auto-sell-prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ dry_run: true })
      });
      const dryData = await dryRes.json();
      
      if (dryData.affected_count === 0) {
        toast.info("No Partner/Agent records need sell_price reset");
        return;
      }
      
      if (!window.confirm(`Found ${dryData.affected_count} Partner/Agent records with incorrect sell prices. Reset all to 0?`)) return;
      
      const res = await fetch(`${BACKEND_URL}/api/sales/reset-auto-sell-prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ dry_run: false })
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Reset ${data.modified_count} Partner/Agent sell prices to 0`);
        fetchSales();
      } else {
        toast.error(data.detail || "Failed to reset sell prices");
      }
    } catch (error) {
      toast.error("Failed to reset sell prices");
    } finally {
      setResettingPartnerSellPrices(false);
    }
  };

  // Fetch all search results for export (bypass pagination)
  const fetchAllSearchResultsForExport = async () => {
    const { tracking, customerName, productName, dateFrom, dateTo, globalSearch } = advancedSearchFilters;
    
    try {
      const params = new URLSearchParams();
      if (tracking) params.append("tracking", tracking);
      if (customerName) params.append("customer_name", customerName);
      if (productName) params.append("product_name", productName);
      if (dateFrom) params.append("start_date", dateFrom);
      if (dateTo) params.append("end_date", dateTo);
      if (globalSearch) params.append("search", globalSearch);
      params.append("limit", 10000); // Get all results for export
      
      const response = await fetch(`${BACKEND_URL}/api/sales?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      return data.sales || [];
    } catch (error) {
      console.error("Error fetching export data:", error);
      return [];
    }
  };

  // Export search results to Excel
  const exportSearchResultsToExcel = async () => {
    setIsExporting(true);
    
    try {
      const data = await fetchAllSearchResultsForExport();
      
      if (data.length === 0) {
        toast.error("No data to export");
        setIsExporting(false);
        return;
      }
      
      // Prepare Excel data
      const excelData = data.map((sale, idx) => ({
        "#": idx + 1,
        "Tracking": sale.tracking || "",
        "Partner Code": sale.client_code || "",
        "Customer Name": sale.customer_name || "",
        "Products": sale.products?.filter(p => p.name && p.name !== 'nan').map(p => p.name).join(", ") || "",
        "Date": formatDDMMYYDisplay(sale.order_date || sale.sale_date),
        "Sell Price (₹)": sale.sell_price || 0,
        "Cost (₹)": sale.cost_price || 0,
        "Profit (₹)": sale.profit || 0,
        "Margin (%)": sale.profit_margin ? sale.profit_margin.toFixed(2) : "0.00",
        "Status": sale.status || ""
      }));
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      ws["!cols"] = [
        { wch: 6 },   // #
        { wch: 18 },  // Tracking
        { wch: 15 },  // Partner Code
        { wch: 25 },  // Customer Name
        { wch: 40 },  // Products
        { wch: 12 },  // Date
        { wch: 14 },  // Sell Price
        { wch: 12 },  // Cost
        { wch: 12 },  // Profit
        { wch: 10 },  // Margin
        { wch: 10 }   // Status
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, "Sales Search Results");
      
      // Generate filename with filters
      const dateStr = new Date().toISOString().split('T')[0];
      let filename = `Sales_Search_${dateStr}`;
      if (advancedSearchFilters.tracking) filename += `_T-${advancedSearchFilters.tracking.substring(0,10)}`;
      if (advancedSearchFilters.customerName) filename += `_C-${advancedSearchFilters.customerName.substring(0,10)}`;
      if (advancedSearchFilters.globalSearch) filename += `_S-${advancedSearchFilters.globalSearch.substring(0,10)}`;
      filename += ".xlsx";
      
      // Save file
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, filename);
      
      toast.success(`Exported ${data.length} records to Excel`);
    } catch (error) {
      toast.error("Export failed");
      console.error("Export error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Export search results to PDF
  const exportSearchResultsToPDF = async () => {
    setIsExporting(true);
    
    try {
      const data = await fetchAllSearchResultsForExport();
      
      if (data.length === 0) {
        toast.error("No data to export");
        setIsExporting(false);
        return;
      }
      
      // Create PDF
      const doc = new jsPDF({ orientation: "landscape" });
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(16, 185, 129); // Emerald color
      doc.text("EAC - Sales Search Results", pageWidth / 2, 15, { align: "center" });
      
      // Subtitle with filters
      doc.setFontSize(10);
      doc.setTextColor(100);
      let filterParts = [];
      if (advancedSearchFilters.tracking) filterParts.push(`Tracking: ${advancedSearchFilters.tracking}`);
      if (advancedSearchFilters.customerName) filterParts.push(`Customer: ${advancedSearchFilters.customerName}`);
      if (advancedSearchFilters.productName) filterParts.push(`Product: ${advancedSearchFilters.productName}`);
      if (advancedSearchFilters.dateFrom) filterParts.push(`From: ${advancedSearchFilters.dateFrom}`);
      if (advancedSearchFilters.dateTo) filterParts.push(`To: ${advancedSearchFilters.dateTo}`);
      if (advancedSearchFilters.globalSearch) filterParts.push(`Search: ${advancedSearchFilters.globalSearch}`);
      
      const subtitle = filterParts.length > 0 
        ? `Filters: ${filterParts.join(" | ")}`
        : `Generated: ${formatDMY(new Date())}`;
      doc.text(subtitle, pageWidth / 2, 22, { align: "center" });
      
      // Summary
      if (searchResultsSummary) {
        doc.setFontSize(9);
        const summaryText = `Total: ${data.length} records | Sales: ₹${searchResultsSummary.total_sales?.toLocaleString()} | Cost: ₹${searchResultsSummary.total_cost?.toLocaleString()} | Profit: ₹${searchResultsSummary.total_profit?.toLocaleString()} | Margin: ${searchResultsSummary.profit_margin}%`;
        doc.text(summaryText, pageWidth / 2, 28, { align: "center" });
      }
      
      // Table data
      const tableData = data.map((sale, idx) => [
        idx + 1,
        sale.tracking || "",
        sale.client_code || "",
        sale.customer_name?.substring(0, 20) || "",
        sale.products?.filter(p => p.name && p.name !== 'nan').slice(0, 2).map(p => p.name).join(", ").substring(0, 30) || "",
        formatDDMMYYDisplay(sale.order_date || sale.sale_date),
        `₹${(sale.sell_price || 0).toLocaleString()}`,
        `₹${(sale.cost_price || 0).toLocaleString()}`,
        `₹${(sale.profit || 0).toLocaleString()}`,
        sale.status || ""
      ]);
      
      // Generate table using autoTable
      autoTable(doc, {
        startY: 34,
        head: [["#", "Tracking", "Partner", "Customer", "Products", "Date", "Sell Price", "Cost", "Profit", "Status"]],
        body: tableData,
        theme: "grid",
        headStyles: {
          fillColor: [16, 185, 129],
          textColor: 255,
          fontSize: 8,
          fontStyle: "bold"
        },
        bodyStyles: {
          fontSize: 7
        },
        columnStyles: {
          0: { cellWidth: 8, halign: "center" },
          1: { cellWidth: 28 },
          2: { cellWidth: 22 },
          3: { cellWidth: 30 },
          4: { cellWidth: 45 },
          5: { cellWidth: 22 },
          6: { cellWidth: 25, halign: "right" },
          7: { cellWidth: 22, halign: "right" },
          8: { cellWidth: 22, halign: "right" },
          9: { cellWidth: 18, halign: "center" }
        },
        margin: { top: 34, left: 10, right: 10 },
        didDrawPage: (pageData) => {
          // Footer with page number
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Page ${pageData.pageNumber}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: "center" }
          );
        }
      });
      
      // Generate filename with filters
      const dateStr = new Date().toISOString().split('T')[0];
      let filename = `Sales_Search_${dateStr}`;
      if (advancedSearchFilters.tracking) filename += `_T-${advancedSearchFilters.tracking.substring(0,10)}`;
      if (advancedSearchFilters.customerName) filename += `_C-${advancedSearchFilters.customerName.substring(0,10)}`;
      filename += ".pdf";
      
      // Save file
      doc.save(filename);
      
      toast.success(`Exported ${data.length} records to PDF`);
    } catch (error) {
      toast.error("Export failed");
      console.error("Export error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Rematch unmatched sales with shipment orders
  const handleRematchUnmatched = async () => {
    if (statusCounts.unmatched === 0) {
      toast.info("No unmatched sales to rematch");
      return;
    }
    
    setIsRematching(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/sales/rematch-unmatched`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.matched_count > 0) {
          toast.success(`Rematched ${data.matched_count} sales with cost data!`);
          // Refresh sales data
          fetchSales();
        } else {
          toast.info("No matching cost data found for unmatched sales");
        }
      } else {
        toast.error(data.detail || "Failed to rematch sales");
      }
    } catch (error) {
      console.error("Error rematching sales:", error);
      toast.error("Failed to rematch sales");
    } finally {
      setIsRematching(false);
    }
  };

  // Build role lookup map
  const roleLookup = useMemo(() => {
    const lookup = {};
    ledgers.forEach(l => {
      lookup[l.client_code?.toUpperCase()] = l.role || "Client";
    });
    return lookup;
  }, [ledgers]);

  // Calculate role-based summaries - only used as fallback when backend summary is unavailable
  const calculateRoleSummaries = useCallback(() => {
    // Backend summary is now the primary source (fetched in fetchSales)
    // This is kept as a no-op since summaries are populated from /api/sales/all-partners-summary
  }, [sales, roleLookup]);

  // Calculate status counts
  const statusCounts = useMemo(() => {
    const counts = { matched: 0, unmatched: 0, total: sales.length };
    sales.forEach(s => {
      if (s.status === 'matched') counts.matched++;
      else if (s.status === 'unmatched') counts.unmatched++;
    });
    return counts;
  }, [sales]);

  // Filter sales based on filterStatus
  const filteredSales = useMemo(() => {
    if (filterStatus === "all") return sales;
    return sales.filter(s => s.status === filterStatus);
  }, [sales, filterStatus]);

  // Get unmatched records for the unmatched records view
  // Exclude tracking_replaced records since they intentionally have zero cost (cost was moved to new tracking)
  const unmatchedRecords = useMemo(() => {
    return sales.filter(s => {
      // Skip records that were replaced tracking numbers - these intentionally have zero cost
      if (s.tracking_replaced === true || s.is_strikethrough === true) {
        return false;
      }
      return s.status === "unmatched" || !s.cost_price || s.cost_price === 0;
    });
  }, [sales]);

  // State for showing unmatched records table
  const [showUnmatchedTable, setShowUnmatchedTable] = useState(false);

  // Filtered summaries based on filterStatus
  const filteredAllClientsSummary = useMemo(() => {
    if (filterStatus === "all") return allClientsSummary;
    return allClientsSummary.filter(client => {
      if (filterStatus === "matched") return client.matched_count > 0;
      if (filterStatus === "unmatched") return client.unmatched_count > 0;
      return true;
    }).map(client => {
      // Recalculate totals based on filter
      if (filterStatus === "matched") {
        const matchedSales = sales.filter(s => 
          s.client_code?.toUpperCase() === client._id?.toUpperCase() && s.status === "matched"
        );
        return {
          ...client,
          total_sales: matchedSales.reduce((sum, s) => sum + (s.sell_price || 0), 0),
          total_cost: matchedSales.reduce((sum, s) => sum + (s.cost_price || 0), 0),
          total_profit: matchedSales.reduce((sum, s) => sum + (s.profit || 0), 0),
          order_count: matchedSales.length
        };
      } else if (filterStatus === "unmatched") {
        const unmatchedSales = sales.filter(s => 
          s.client_code?.toUpperCase() === client._id?.toUpperCase() && s.status === "unmatched"
        );
        return {
          ...client,
          total_sales: unmatchedSales.reduce((sum, s) => sum + (s.sell_price || 0), 0),
          total_cost: unmatchedSales.reduce((sum, s) => sum + (s.cost_price || 0), 0),
          total_profit: unmatchedSales.reduce((sum, s) => sum + (s.profit || 0), 0),
          order_count: unmatchedSales.length
        };
      }
      return client;
    });
  }, [allClientsSummary, filterStatus, sales]);

  const filteredClientSummary = useMemo(() => {
    if (filterStatus === "all") return clientSummary;
    return clientSummary.filter(client => {
      if (filterStatus === "matched") return client.matched_count > 0;
      if (filterStatus === "unmatched") return client.unmatched_count > 0;
      return true;
    });
  }, [clientSummary, filterStatus]);

  const filteredPartnerSummary = useMemo(() => {
    if (filterStatus === "all") return partnerSummary;
    return partnerSummary.filter(client => {
      if (filterStatus === "matched") return client.matched_count > 0;
      if (filterStatus === "unmatched") return client.unmatched_count > 0;
      return true;
    });
  }, [partnerSummary, filterStatus]);

  const filteredAgentSummary = useMemo(() => {
    if (filterStatus === "all") return agentSummary;
    return agentSummary.filter(client => {
      if (filterStatus === "matched") return client.matched_count > 0;
      if (filterStatus === "unmatched") return client.unmatched_count > 0;
      return true;
    });
  }, [agentSummary, filterStatus]);

  // Fetch upload history
  const fetchUploadHistory = useCallback(async () => {
    setUploadHistoryLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/sales/by-uploader`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setUploadHistory(data.uploads || []);
      }
    } catch (error) {
      console.error("Error fetching upload history:", error);
      toast.error("Failed to load upload history");
    } finally {
      setUploadHistoryLoading(false);
    }
  }, [token]);

  // Fetch raw data for a specific upload with server-side sorting and search
  const fetchUploadRawData = async (upload, page = 1, sortField = null, sortOrder = 'asc', search = '') => {
    setSelectedUpload(upload);
    setUploadRawDataLoading(true);
    setShowUploadRawDataDialog(true);
    setRawDataPage(page);
    
    // Update state for sort/search
    if (sortField !== undefined) setRawDataSortField(sortField);
    if (sortOrder !== undefined) setRawDataSortOrder(sortOrder);
    if (search !== undefined) setRawDataSearch(search);
    
    try {
      const skip = (page - 1) * rawDataPageSize;
      let url = `${BACKEND_URL}/api/sales/upload-data/${upload.upload_date}?limit=${rawDataPageSize}&skip=${skip}`;
      if (upload.uploaded_by && upload.uploaded_by !== 'Unknown') {
        url += `&uploaded_by=${encodeURIComponent(upload.uploaded_by)}`;
      }
      // Add sorting parameters
      if (sortField) {
        url += `&sort_field=${encodeURIComponent(sortField)}&sort_order=${encodeURIComponent(sortOrder)}`;
      }
      // Add search parameter
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setUploadRawData(data.sales || []);
        setUploadRawDataTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching raw data:", error);
      toast.error("Failed to load raw data");
    } finally {
      setUploadRawDataLoading(false);
    }
  };

  // Handle page change for raw data
  const handleRawDataPageChange = (newPage) => {
    if (selectedUpload) {
      fetchUploadRawData(selectedUpload, newPage, rawDataSortField, rawDataSortOrder, rawDataSearch);
    }
  };
  
  // Handle server-side sorting
  const handleRawDataSort = (field) => {
    if (!selectedUpload) return;
    const newOrder = rawDataSortField === field && rawDataSortOrder === 'asc' ? 'desc' : 'asc';
    fetchUploadRawData(selectedUpload, 1, field, newOrder, rawDataSearch);
  };
  
  // Handle server-side search with debounce
  const handleRawDataSearchChange = (value) => {
    setRawDataSearch(value);
  };
  
  // Trigger search on Enter or after typing stops
  const handleRawDataSearchSubmit = () => {
    if (selectedUpload) {
      fetchUploadRawData(selectedUpload, 1, rawDataSortField, rawDataSortOrder, rawDataSearch);
    }
  };

  useEffect(() => {
    fetchSales();
    fetchLedgers();
    fetchGpData();
    fetchReconciliationData();
    fetchUploadHistory();
    fetchPendingApprovals();
  }, [fetchSales, fetchLedgers, fetchGpData, fetchReconciliationData, fetchPendingApprovals]);

  useEffect(() => {
    calculateRoleSummaries();
  }, [calculateRoleSummaries]);

  // Fetch GP code data when code is selected
  useEffect(() => {
    if (gpSelectedCode) {
      fetchGpCodeData(gpSelectedCode, gpSelectedYear, gpSelectedMonth);
    }
  }, [gpSelectedCode, gpSelectedYear, gpSelectedMonth, fetchGpCodeData]);

  // Refetch reconciliation when date filters change
  useEffect(() => {
    if (activeTab === "ledger-reconciliation") {
      fetchReconciliationData();
    }
  }, [reconciliationDateFrom, reconciliationDateTo, activeTab, fetchReconciliationData]);

  // GP Navigation functions
  const gpBackToSummary = () => {
    setGpViewLevel("summary");
    setGpSelectedCode(null);
    setGpCodeData(null);
    setGpSelectedYear(null);
    setGpSelectedMonth(null);
  };

  const gpSelectCode = (code) => {
    setGpSelectedCode(code);
    setGpViewLevel("code");
    setGpSelectedYear(null);
    setGpSelectedMonth(null);
  };

  const gpSelectYear = (year) => {
    setGpSelectedYear(year);
    setGpViewLevel("year");
    setGpSelectedMonth(null);
  };

  const gpSelectMonth = (month) => {
    setGpSelectedMonth(month);
    setGpViewLevel("month");
  };

  const gpGoBack = () => {
    if (gpViewLevel === "month") {
      setGpViewLevel("year");
      setGpSelectedMonth(null);
    } else if (gpViewLevel === "year") {
      setGpViewLevel("code");
      setGpSelectedYear(null);
    } else if (gpViewLevel === "code") {
      gpBackToSummary();
    }
  };

  // Get client's sales - use backend drill-down data (PLU-sourced, deduped, complete)
  const clientSales = useMemo(() => {
    if (!selectedClient) return [];
    // Use backend drill-down data if available (accurate, complete data from PLU)
    if (partnerDrilldownSales.length > 0) {
      return partnerDrilldownSales;
    }
    // Fallback to local sales filter
    return sales.filter(s => s.client_code?.toUpperCase() === selectedClient.toUpperCase());
  }, [sales, selectedClient, partnerDrilldownSales]);

  // Calculate drill-down data for selected client
  const clientDrilldownData = useMemo(() => {
    if (!selectedClient || clientSales.length === 0) return [];

    // Helper to get date string from sale (prefer order_date, fallback to sale_date)
    const getDateStr = (sale) => sale.order_date || sale.sale_date || "";
    
    // Helper to extract year from DDMMYY format
    const getYearFromDate = (dateStr) => {
      if (!dateStr) return "Unknown";
      if (dateStr.length === 6) {
        return "20" + dateStr.substring(4, 6);
      } else if (dateStr.includes("-")) {
        return dateStr.split("-")[0];
      }
      return "Unknown";
    };
    
    // Helper to extract month from DDMMYY format
    const getMonthFromDate = (dateStr) => {
      if (!dateStr) return "01";
      if (dateStr.length === 6) {
        return dateStr.substring(2, 4);
      } else if (dateStr.includes("-")) {
        return dateStr.split("-")[1] || "01";
      }
      return "01";
    };

    if (clientViewLevel === "year") {
      // Group by year
      const yearData = {};
      clientSales.forEach(sale => {
        const dateStr = getDateStr(sale);
        const year = getYearFromDate(dateStr);
        
        if (!yearData[year]) {
          yearData[year] = { period: year, sell_price: 0, cost_price: 0, profit: 0, order_count: 0 };
        }
        yearData[year].sell_price += sale.sell_price || 0;
        yearData[year].cost_price += sale.cost_price || 0;
        yearData[year].profit += sale.profit || 0;
        yearData[year].order_count += 1;
      });
      
      return Object.values(yearData)
        .map(d => ({ ...d, profit_margin: d.sell_price > 0 ? (d.profit / d.sell_price * 100) : 0 }))
        .sort((a, b) => b.period.localeCompare(a.period));
        
    } else if (clientViewLevel === "month" && selectedYear) {
      // Group by month
      const monthData = {};
      clientSales.forEach(sale => {
        const dateStr = getDateStr(sale);
        const year = getYearFromDate(dateStr);
        const month = getMonthFromDate(dateStr);
        
        if (year !== selectedYear) return;
        
        const key = `${year}-${month}`;
        if (!monthData[key]) {
          monthData[key] = { period: key, month, sell_price: 0, cost_price: 0, profit: 0, order_count: 0 };
        }
        monthData[key].sell_price += sale.sell_price || 0;
        monthData[key].cost_price += sale.cost_price || 0;
        monthData[key].profit += sale.profit || 0;
        monthData[key].order_count += 1;
      });
      
      return Object.values(monthData)
        .map(d => ({ ...d, profit_margin: d.sell_price > 0 ? (d.profit / d.sell_price * 100) : 0 }))
        .sort((a, b) => b.period.localeCompare(a.period));
        
    } else if (clientViewLevel === "date" && selectedYear && selectedMonth) {
      // Group by date
      const dateData = {};
      clientSales.forEach(sale => {
        const dateStr = getDateStr(sale);
        const year = getYearFromDate(dateStr);
        const month = getMonthFromDate(dateStr);
        let day = "01";
        
        if (dateStr.length === 6) {
          day = dateStr.substring(0, 2);
        } else if (dateStr.includes("-")) {
          const parts = dateStr.split("-");
          day = parts[2] || "01";
        }
        
        if (year !== selectedYear || month !== selectedMonth) return;
        
        const key = `${year}-${month}-${day}`;
        if (!dateData[key]) {
          dateData[key] = { period: key, day, sell_price: 0, cost_price: 0, profit: 0, order_count: 0, sales: [] };
        }
        dateData[key].sell_price += sale.sell_price || 0;
        dateData[key].cost_price += sale.cost_price || 0;
        dateData[key].profit += sale.profit || 0;
        dateData[key].order_count += 1;
        dateData[key].sales.push(sale);
      });
      
      return Object.values(dateData)
        .map(d => ({ ...d, profit_margin: d.sell_price > 0 ? (d.profit / d.sell_price * 100) : 0 }))
        .sort((a, b) => b.period.localeCompare(a.period));
    }
    
    return [];
  }, [clientSales, clientViewLevel, selectedYear, selectedMonth, selectedClient]);

  // Handle client code click - enter drill-down (fetch data from backend)
  const handleClientClick = async (clientCode) => {
    setSelectedClient(clientCode);
    setClientViewLevel("year");
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDate(null);
    
    // Fetch accurate drill-down data from backend (PLU-sourced, deduped)
    setDrilldownLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/sales/partner-drilldown/${encodeURIComponent(clientCode)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setPartnerDrilldownSales(data.sales || []);
      } else {
        setPartnerDrilldownSales([]);
      }
    } catch (err) {
      console.error("Drilldown fetch error:", err);
      setPartnerDrilldownSales([]);
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Handle drill-down navigation
  const handlePeriodClick = (period) => {
    if (clientViewLevel === "year") {
      setSelectedYear(period);
      setClientViewLevel("month");
    } else if (clientViewLevel === "month") {
      const month = period.split("-")[1];
      setSelectedMonth(month);
      setClientViewLevel("date");
      setSelectedDate(null);
    }
  };

  // Handle date row click - toggle expand/collapse
  const handleDateClick = (period) => {
    setSelectedDate(selectedDate === period ? null : period);
  };

  // Go back in drill-down
  const goBack = () => {
    if (clientViewLevel === "date") {
      setClientViewLevel("month");
      setSelectedMonth(null);
      setSelectedDate(null);
    } else if (clientViewLevel === "month") {
      setClientViewLevel("year");
      setSelectedYear(null);
    } else if (clientViewLevel === "year") {
      setSelectedClient(null);
      setClientViewLevel("list");
    }
  };

  // Back to list view
  const backToList = () => {
    setSelectedClient(null);
    setClientViewLevel("list");
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDate(null);
  };

  // Delete entries from Sales drill-down (permanent exclusion)
  const handleDeleteDrilldownEntries = async () => {
    if (!deletePassword || !selectedClient || entriesToDelete.length === 0) return;
    
    setDeletingDrilldownEntries(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/sales/partner-drilldown/delete-entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          password: deletePassword,
          partner_code: selectedClient,
          tracking_numbers: entriesToDelete.map(e => e.tracking),
          reason: "Deleted from Sales drill-down"
        })
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        toast.success(`Deleted ${data.deleted_count} entries from ${selectedClient}`);
        setShowDeleteDrilldownDialog(false);
        setDeletePassword("");
        setEntriesToDelete([]);
        // Refresh drill-down data
        handleClientClick(selectedClient);
      } else {
        toast.error(data.detail || "Delete failed");
      }
    } catch (err) {
      toast.error("Delete failed: " + err.message);
    } finally {
      setDeletingDrilldownEntries(false);
    }
  };

  // Initiate delete for a group of entries (month or date level)
  const initDeleteEntries = (entries) => {
    setEntriesToDelete(entries);
    setDeletePassword("");
    setShowDeleteDrilldownDialog(true);
  };

  // Fetch deleted entries for viewing/restoring
  const fetchDeletedEntries = async () => {
    if (!selectedClient) return;
    try {
      const resp = await fetch(`${BACKEND_URL}/api/sales/partner-drilldown/${encodeURIComponent(selectedClient)}/deleted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setDeletedEntries(data.deleted_entries || []);
        setShowDeletedEntriesDialog(true);
      }
    } catch (err) {
      toast.error("Failed to fetch deleted entries");
    }
  };

  // Restore deleted entries
  const handleRestoreEntries = async (trackingsToRestore) => {
    if (!restorePassword || !selectedClient) return;
    
    setRestoringEntries(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/sales/partner-drilldown/restore-entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          password: restorePassword,
          partner_code: selectedClient,
          tracking_numbers: trackingsToRestore
        })
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        toast.success(`Restored ${data.restored_count} entries`);
        setRestorePassword("");
        // Refresh both views
        fetchDeletedEntries();
        handleClientClick(selectedClient);
      } else {
        toast.error(data.detail || "Restore failed");
      }
    } catch (err) {
      toast.error("Restore failed: " + err.message);
    } finally {
      setRestoringEntries(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const url = `${BACKEND_URL}/api/sales/upload?replace_existing=${replaceExisting}`;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Uploaded ${data.matched} matched, ${data.unmatched} unmatched records`);
        setShowUploadDialog(false);
        fetchSales();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Calculate sales from orders
  // State for calculation progress
  const [calcProgress, setCalcProgress] = useState({ current: 0, total: 0, isRunning: false });
  
  const handleCalculateFromOrders = async () => {
    setCalculating(true);
    setCalcProgress({ current: 0, total: 0, isRunning: true });
    
    let allSales = [];
    let skip = 0;
    const batchSize = 10000;
    let hasMore = true;
    let totalOrders = 0;
    let overallSummary = { total_sales: 0, total_cost: 0, total_profit: 0, clients_processed: 0 };
    
    try {
      while (hasMore) {
        const response = await fetch(`${BACKEND_URL}/api/sales/calculate-from-orders?skip=${skip}&limit=${batchSize}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ client_code: null })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          toast.error(data.detail || "Calculation failed");
          break;
        }
        
        // Accumulate results
        allSales = [...allSales, ...(data.sales || [])];
        totalOrders = data.pagination?.total_orders || totalOrders;
        hasMore = data.pagination?.has_more || false;
        
        // Update summary totals
        overallSummary.total_sales += data.summary?.total_sales || 0;
        overallSummary.total_cost += data.summary?.total_cost || 0;
        overallSummary.total_profit += data.summary?.total_profit || 0;
        overallSummary.clients_processed = Math.max(overallSummary.clients_processed, data.summary?.clients_processed || 0);
        
        // Update progress
        setCalcProgress({ 
          current: allSales.length, 
          total: totalOrders,
          isRunning: hasMore
        });
        
        if (hasMore) {
          toast.info(`Calculated ${allSales.length.toLocaleString()} of ${totalOrders.toLocaleString()} orders...`);
          skip += batchSize;
          // Small delay between batches
          await new Promise(r => setTimeout(r, 300));
        }
      }
      
      overallSummary.total_records = allSales.length;
      overallSummary.profit_margin = overallSummary.total_sales > 0 
        ? ((overallSummary.total_profit / overallSummary.total_sales) * 100).toFixed(2)
        : 0;
      
      setCalculatedSales(allSales);
      setCalculatedSummary(overallSummary);
      setShowCalculateDialog(true);
      
      toast.success(`Calculated ${allSales.length.toLocaleString()} sales records`);
    } catch (error) {
      console.error("Calculate error:", error);
      toast.error("Calculation failed");
    } finally {
      setCalculating(false);
      setCalcProgress({ current: 0, total: 0, isRunning: false });
    }
  };

  // Save calculated sales
  const handleSaveCalculated = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/sales/save-calculated`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          sales: calculatedSales,
          replace_existing: replaceExisting
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setShowCalculateDialog(false);
        setCalculatedSales([]);
        fetchSales();
      } else {
        toast.error(data.detail || "Save failed");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Save failed");
    }
  };

  // Delete all sales
  const handleDeleteAll = async () => {
    if (!window.confirm("Delete ALL sales records? This cannot be undone.")) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/sales`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        fetchSales();
        backToList();
      } else {
        toast.error(data.detail || "Delete failed");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Delete failed");
    }
  };

  // Generate DP Client Sales from shipment orders
  const handleGenerateDpClientSales = async () => {
    setGeneratingDpSales(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/sales/auto-generate-dp-client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(dpSalesYear === "all" ? {} : { year: Number(dpSalesYear) })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setDpSalesResult(data);
        setShowDpSalesResultDialog(true);
        
        if (data.generated > 0) {
          toast.success(`Generated ${data.generated} sales records for DP Clients`);
          fetchSales();
        } else if (data.skipped_existing > 0) {
          toast.info(`All ${data.skipped_existing} DP Client orders already have sales records`);
        } else {
          toast.warning(data.message || "No DP Client orders found to process");
        }
      } else {
        toast.error(data.detail || "Failed to generate DP Client sales");
      }
    } catch (error) {
      console.error("DP Client sales generation error:", error);
      toast.error("Failed to generate DP Client sales");
    } finally {
      setGeneratingDpSales(false);
    }
  };

  // Regenerate DP Client Sales (delete and recreate)
  const handleRegenerateDpClientSales = async (clientCodes = []) => {
    if (!clientCodes.length) {
      // Get all DP client codes from clientSummary
      clientCodes = clientSummary.map(c => c._id);
    }
    
    if (!clientCodes.length) {
      toast.warning("No DP Clients found to regenerate");
      return;
    }
    
    if (!window.confirm(`This will DELETE and REGENERATE sales for ${clientCodes.length} DP Client(s). Continue?`)) {
      return;
    }
    
    setGeneratingDpSales(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/sales/regenerate-dp-client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ client_codes: clientCodes })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setDpSalesResult(data);
        setShowDpSalesResultDialog(true);
        toast.success(`Regenerated: Deleted ${data.deleted_old_records}, Created ${data.generated_new_records} records`);
        fetchSales();
      } else {
        toast.error(data.detail || "Failed to regenerate DP Client sales");
      }
    } catch (error) {
      console.error("DP Client sales regeneration error:", error);
      toast.error("Failed to regenerate DP Client sales");
    } finally {
      setGeneratingDpSales(false);
    }
  };

  // Export missing products from DP Sales generation to CSV
  const handleExportMissingProducts = () => {
    if (!dpSalesResult?.errors?.length) {
      toast.warning("No missing products to export");
      return;
    }

    // Create CSV with ALL entries (including tracking numbers)
    const csvRows = [
      ["Client Code", "Tracking Number", "Product Name", "Error Type", "Action Required"]
    ];

    dpSalesResult.errors.forEach(err => {
      csvRows.push([
        err.client_code || "UNKNOWN",
        err.tracking || "",
        err.product_name || "",
        "Missing Price",
        "Add to DP Client Price List"
      ]);
    });

    // Convert to CSV string
    const csvContent = csvRows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")
    ).join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `missing_dp_client_products_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${dpSalesResult.errors.length} missing product entries`);
  };

  // Prepare bulk import data from DP Sales errors
  const handlePrepareBulkImport = () => {
    if (!dpSalesResult?.errors?.length) {
      toast.warning("No missing products to import");
      return;
    }

    // Group by client and dedupe products
    const errorsByClient = {};
    dpSalesResult.errors.forEach(err => {
      const clientCode = err.client_code || "UNKNOWN";
      if (!errorsByClient[clientCode]) {
        errorsByClient[clientCode] = new Map();
      }
      // Use Map to dedupe by product name
      if (!errorsByClient[clientCode].has(err.product_name)) {
        errorsByClient[clientCode].set(err.product_name, {
          client_code: clientCode,
          product_name: err.product_name,
          sell_price: ""
        });
      }
    });

    // Flatten to array
    const importData = [];
    Object.values(errorsByClient).forEach(products => {
      products.forEach(product => importData.push(product));
    });

    setBulkImportData(importData);
    setShowBulkImportDialog(true);
  };

  // Update price in bulk import data
  const updateBulkImportPrice = (index, price) => {
    setBulkImportData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], sell_price: price };
      return updated;
    });
  };

  // Submit bulk import
  const handleBulkImport = async () => {
    // Filter only items with prices
    const productsWithPrices = bulkImportData.filter(p => p.sell_price && !isNaN(parseFloat(p.sell_price)));
    
    if (productsWithPrices.length === 0) {
      toast.error("Please enter at least one sell price");
      return;
    }

    setImportingProducts(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/dp-client/bulk-import-products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          products: productsWithPrices.map(p => ({
            client_code: p.client_code,
            product_name: p.product_name,
            sell_price: parseFloat(p.sell_price)
          })),
          fix_orders: true
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Imported ${data.results.added} new, updated ${data.results.updated} products`);
        setShowBulkImportDialog(false);
        setBulkImportData([]);
        // Regenerate DP sales after import
        if (data.results.added > 0 || data.results.updated > 0) {
          toast.info("Regenerating DP Client sales with new prices...");
          handleGenerateDpClientSales();
        }
      } else {
        toast.error(data.detail || "Import failed");
      }
    } catch (error) {
      console.error("Bulk import error:", error);
      toast.error("Import failed");
    } finally {
      setImportingProducts(false);
    }
  };

  // Partner sales upload
  const handlePartnerUpload = async (e, partnerCode) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const url = `${BACKEND_URL}/api/sales/partner/${partnerCode}/upload?replace_existing=${replaceExisting}`;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Partner ${partnerCode}: ${data.matched} matched, ${data.unmatched} unmatched`);
        setShowPartnerUploadDialog(false);
        setSelectedPartnerForUpload(null);
        fetchSales();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      console.error("Partner upload error:", error);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Raw Partner Sheets Upload (Multi-sheet format with partner names as tab names)
  const handleRawPartnerSheetsUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const url = `${BACKEND_URL}/api/sales/upload-raw-partner-sheets?replace_existing=${replaceExisting}`;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const partnerCount = Object.keys(data.partner_summaries || {}).length;
        const totalRecords = data.total_inserted || 0;
        const totalProfit = data.summary?.total_profit || 0;
        toast.success(`✅ Uploaded ${totalRecords} records from ${partnerCount} partners. Total GP: ₹${totalProfit.toLocaleString()}`);
        setShowRawPartnerUploadDialog(false);
        fetchSales();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      console.error("Raw partner upload error:", error);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Agent bulk sales upload
  const handleAgentBulkUpload = async (e, agentCode) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const url = `${BACKEND_URL}/api/sales/agent/${agentCode}/bulk-upload?replace_existing=${replaceExisting}`;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Agent ${agentCode}: ${data.matched} records for ${data.clients_processed?.length || 0} clients`);
        setShowAgentUploadDialog(false);
        setSelectedAgentForUpload(null);
        fetchSales();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      console.error("Agent upload error:", error);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Regenerate sales for a DP Client (fixes cost duplication issue)
  const handleRegenerateDPClientSales = async (clientCode) => {
    if (!clientCode) return;
    
    setRegeneratingSales(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/sales/regenerate-dp-client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          client_codes: [clientCode]
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Regenerated ${data.generated_new_records} sales records (deleted ${data.deleted_old_records} old records)`);
        // Refresh sales data
        fetchSales();
      } else {
        toast.error(data.detail || "Failed to regenerate sales");
      }
    } catch (error) {
      console.error("Regenerate sales error:", error);
      toast.error("Failed to regenerate sales");
    } finally {
      setRegeneratingSales(false);
    }
  };

  // Sorting for client list
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: null };
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    if (sortConfig.direction === 'asc') return <ArrowUp className="w-3 h-3 ml-1 text-emerald-600" />;
    if (sortConfig.direction === 'desc') return <ArrowDown className="w-3 h-3 ml-1 text-emerald-600" />;
    return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
  };

  // Filter and sort client list
  const filterAndSortClients = (data) => {
    let result = [...data];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c => c._id?.toLowerCase().includes(term));
    }
    
    if (sortConfig.key && sortConfig.direction) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Use monthly sorting for date fields
        const dateFields = ['date', 'order_date', 'sale_date', 'payment_date', 'created_at'];
        if (dateFields.includes(sortConfig.key)) {
          return compareByMonthYear(aVal, bVal, sortConfig.direction);
        }
        
        if (aVal == null) aVal = 0;
        if (bVal == null) bVal = 0;
        
        // Numeric comparison
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = String(bVal).toLowerCase();
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatMonth = (monthStr) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthNum = parseInt(monthStr) - 1;
    return months[monthNum] || monthStr;
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "Partner":
        return <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">Partner</span>;
      case "Agent":
        return <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">Agent</span>;
      case "Client":
        return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">DP CLIENT</span>;
      default:
        return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">DP CLIENT</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "matched":
        return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Matched</span>;
      case "unmatched":
        return <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full flex items-center gap-1"><XCircle className="w-3 h-3" />Unmatched</span>;
      case "calculated":
        return <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full flex items-center gap-1"><Calculator className="w-3 h-3" />Calculated</span>;
      default:
        return <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-700 rounded-full">{status}</span>;
    }
  };

  // Render partner list table
  const renderClientList = (data, showRole = false) => {
    const filteredData = filterAndSortClients(data);
    
    return (
      <div className="bg-white rounded-lg border relative">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-slate-100 sticky top-0 z-10">
            <tr className="whitespace-nowrap">
              <th className="px-2 sm:px-4 py-3 text-left font-semibold text-slate-700">#</th>
              <th 
                className="px-2 sm:px-4 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200"
                onClick={() => handleSort('_id')}
              >
                <span className="flex items-center">Code {getSortIcon('_id')}</span>
              </th>
              {showRole && (
                <th className="px-4 py-3 text-center font-semibold text-slate-700">Role</th>
              )}
              <th 
                className="px-4 py-3 text-right font-semibold text-slate-700 cursor-pointer hover:bg-slate-200"
                onClick={() => handleSort('order_count')}
              >
                <span className="flex items-center justify-end">Orders {getSortIcon('order_count')}</span>
              </th>
              <th 
                className="px-4 py-3 text-right font-semibold text-amber-700 cursor-pointer hover:bg-slate-200"
                onClick={() => handleSort('unmatched_count')}
              >
                <span className="flex items-center justify-end">Unmatched {getSortIcon('unmatched_count')}</span>
              </th>
              <th 
                className="px-4 py-3 text-right font-semibold text-slate-700 cursor-pointer hover:bg-slate-200"
                onClick={() => handleSort('total_sales')}
              >
                <span className="flex items-center justify-end">Total Sales {getSortIcon('total_sales')}</span>
              </th>
              <th 
                className="px-4 py-3 text-right font-semibold text-slate-700 cursor-pointer hover:bg-slate-200"
                onClick={() => handleSort('total_cost')}
              >
                <span className="flex items-center justify-end">Total Cost {getSortIcon('total_cost')}</span>
              </th>
              <th 
                className="px-4 py-3 text-right font-semibold text-slate-700 cursor-pointer hover:bg-slate-200"
                onClick={() => handleSort('total_profit')}
              >
                <span className="flex items-center justify-end">Total Profit {getSortIcon('total_profit')}</span>
              </th>
              <th className="px-4 py-3 text-right font-semibold text-slate-700">Margin</th>
              <th className="px-4 py-3 text-center font-semibold text-slate-700">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item, idx) => {
              const margin = item.total_sales > 0 ? (item.total_profit / item.total_sales * 100) : 0;
              return (
                <tr 
                  key={item._id || idx} 
                  className="border-t hover:bg-slate-50 cursor-pointer"
                  onClick={() => handleClientClick(item._id)}
                >
                  <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-semibold text-slate-800">{item._id || "Unknown"}</td>
                  {showRole && (
                    <td className="px-4 py-3 text-center">{getRoleBadge(item.role)}</td>
                  )}
                  <td className="px-4 py-3 text-right">{item.order_count}</td>
                  <td className="px-4 py-3 text-right">
                    {item.unmatched_count > 0 ? (
                      <span className="text-amber-600 font-medium">{item.unmatched_count}</span>
                    ) : (
                      <span className="text-slate-300">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(item.total_sales)}</td>
                  <td className="px-4 py-3 text-right text-orange-600">{formatCurrency(item.total_cost)}</td>
                  <td className={`px-4 py-3 text-right font-semibold ${item.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(item.total_profit)}
                  </td>
                  <td className={`px-4 py-3 text-right ${margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {margin.toFixed(1)}%
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ChevronRight className="w-4 h-4 inline text-slate-400" />
                  </td>
                </tr>
              );
            })}
          </tbody>
          {filteredData.length > 0 && (
            <tfoot className="bg-slate-200 font-bold">
              <tr>
                <td colSpan={showRole ? 3 : 2} className="px-4 py-3 text-right">Total ({filteredData.length}):</td>
                <td className="px-4 py-3 text-right">{filteredData.reduce((s, i) => s + i.order_count, 0)}</td>
                <td className="px-4 py-3 text-right text-amber-700">{filteredData.reduce((s, i) => s + (i.unmatched_count || 0), 0)}</td>
                <td className="px-4 py-3 text-right text-blue-700">{formatCurrency(filteredData.reduce((s, i) => s + i.total_sales, 0))}</td>
                <td className="px-4 py-3 text-right text-orange-700">{formatCurrency(filteredData.reduce((s, i) => s + i.total_cost, 0))}</td>
                <td className="px-4 py-3 text-right text-emerald-700">{formatCurrency(filteredData.reduce((s, i) => s + i.total_profit, 0))}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
        </div>
        {filteredData.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            No data available
          </div>
        )}
      </div>
    );
  };

  // Render search results view
  const renderSearchResults = () => {
    const totalPages = Math.ceil(searchResultsTotal / SEARCH_PAGE_SIZE);
    
    return (
      <div className="space-y-4" data-testid="search-results">
        {/* Header with back button and summary */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearAdvancedSearch} 
              className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="search-results-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Summary
            </Button>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <Search className="w-3 h-3 mr-1" />
              Search Results: {searchResultsTotal} records
            </Badge>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Export buttons */}
            <Button
              variant="outline"
              size="sm"
              onClick={exportSearchResultsToExcel}
              disabled={isExporting || searchResults.length === 0}
              className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
              data-testid="export-excel-btn"
            >
              {isExporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportSearchResultsToPDF}
              disabled={isExporting || searchResults.length === 0}
              className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
              data-testid="export-pdf-btn"
            >
              {isExporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              PDF
            </Button>
          </div>
        </div>
        
        {/* Summary Stats */}
        {searchResultsSummary && (
          <div className="flex items-center justify-end gap-4 text-sm">
            <span className="text-blue-600">Sales: <strong>{formatCurrency(searchResultsSummary.total_sales)}</strong></span>
            <span className="text-orange-600">Cost: <strong>{formatCurrency(searchResultsSummary.total_cost)}</strong></span>
            <span className="text-emerald-600">Profit: <strong>{formatCurrency(searchResultsSummary.total_profit)}</strong></span>
            <span className="text-purple-600">Margin: <strong>{searchResultsSummary.profit_margin}%</strong></span>
          </div>
        )}
        
        {/* Active Filters Display */}
        <div className="flex flex-wrap gap-2">
          {advancedSearchFilters.globalSearch && (
            <Badge variant="secondary" className="gap-1">
              Search: &quot;{advancedSearchFilters.globalSearch}&quot;
              <X className="w-3 h-3 cursor-pointer" onClick={() => {
                setAdvancedSearchFilters(prev => ({...prev, globalSearch: ""}));
                performAdvancedSearch(1);
              }} />
            </Badge>
          )}
          {advancedSearchFilters.tracking && (
            <Badge variant="secondary" className="gap-1">
              Tracking: {advancedSearchFilters.tracking}
              <X className="w-3 h-3 cursor-pointer" onClick={() => {
                setAdvancedSearchFilters(prev => ({...prev, tracking: ""}));
                performAdvancedSearch(1);
              }} />
            </Badge>
          )}
          {advancedSearchFilters.customerName && (
            <Badge variant="secondary" className="gap-1">
              Customer: {advancedSearchFilters.customerName}
              <X className="w-3 h-3 cursor-pointer" onClick={() => {
                setAdvancedSearchFilters(prev => ({...prev, customerName: ""}));
                performAdvancedSearch(1);
              }} />
            </Badge>
          )}
          {advancedSearchFilters.productName && (
            <Badge variant="secondary" className="gap-1">
              Product: {advancedSearchFilters.productName}
              <X className="w-3 h-3 cursor-pointer" onClick={() => {
                setAdvancedSearchFilters(prev => ({...prev, productName: ""}));
                performAdvancedSearch(1);
              }} />
            </Badge>
          )}
          {advancedSearchFilters.dateFrom && (
            <Badge variant="secondary" className="gap-1">
              From: {advancedSearchFilters.dateFrom}
              <X className="w-3 h-3 cursor-pointer" onClick={() => {
                setAdvancedSearchFilters(prev => ({...prev, dateFrom: ""}));
                performAdvancedSearch(1);
              }} />
            </Badge>
          )}
          {advancedSearchFilters.dateTo && (
            <Badge variant="secondary" className="gap-1">
              To: {advancedSearchFilters.dateTo}
              <X className="w-3 h-3 cursor-pointer" onClick={() => {
                setAdvancedSearchFilters(prev => ({...prev, dateTo: ""}));
                performAdvancedSearch(1);
              }} />
            </Badge>
          )}
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">#</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Tracking</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Partner</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Customer</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Products</th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700">Date</th>
                <th className="px-3 py-3 text-right font-semibold text-slate-700">Sell Price</th>
                <th className="px-3 py-3 text-right font-semibold text-slate-700">Cost</th>
                <th className="px-3 py-3 text-right font-semibold text-slate-700">Profit</th>
                <th className="px-3 py-3 text-center font-semibold text-slate-700">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {searchResults.map((sale, idx) => (
                <tr 
                  key={sale.id || idx} 
                  className="hover:bg-slate-50"
                  data-testid={`search-result-row-${idx}`}
                >
                  <td className="px-3 py-2 text-slate-500">{(searchResultsPage - 1) * SEARCH_PAGE_SIZE + idx + 1}</td>
                  <td className="px-3 py-2">
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">{sale.tracking || '-'}</code>
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-800">{sale.client_code || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{sale.customer_name || '-'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 max-w-[200px] truncate" title={sale.products?.filter(p => p.name && p.name !== 'nan').map(p => p.name).join(', ')}>
                    {sale.products?.filter(p => p.name && p.name !== 'nan').slice(0, 2).map(p => p.name).join(', ') || '-'}
                    {sale.products?.filter(p => p.name && p.name !== 'nan').length > 2 && ' ...'}
                  </td>
                  <td className="px-3 py-2 text-center text-slate-600">{formatDDMMYYDisplay(sale.order_date || sale.sale_date)}</td>
                  <td className="px-3 py-2 text-right text-blue-600">{formatCurrency(sale.sell_price || 0)}</td>
                  <td className="px-3 py-2 text-right text-orange-600">{formatCurrency(sale.cost_price || 0)}</td>
                  <td className="px-3 py-2 text-right text-emerald-600 font-medium">{formatCurrency(sale.profit || 0)}</td>
                  <td className="px-3 py-2 text-center">{getStatusBadge(sale.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {searchResults.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Search className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="font-medium">No matching records found</p>
              <p className="text-sm mt-1">Try adjusting your search filters</p>
            </div>
          )}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-2">
            <p className="text-sm text-slate-500">
              Showing {(searchResultsPage - 1) * SEARCH_PAGE_SIZE + 1} - {Math.min(searchResultsPage * SEARCH_PAGE_SIZE, searchResultsTotal)} of {searchResultsTotal}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={searchResultsPage === 1 || isSearching}
                onClick={() => performAdvancedSearch(searchResultsPage - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-sm text-slate-600">
                Page {searchResultsPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={searchResultsPage >= totalPages || isSearching}
                onClick={() => performAdvancedSearch(searchResultsPage + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render client drill-down view (Year → Month → Date → Orders)
  const renderClientDrilldown = () => {
    const clientInfo = allClientsSummary.find(c => c._id?.toUpperCase() === selectedClient?.toUpperCase());
    const isClientRole = clientInfo?.role === "Client";
    
    return (
      <div className="space-y-4">
        {/* Header with back button and client info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={goBack} className="gap-1 border-slate-300 hover:bg-slate-100">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">
                {clientViewLevel === "date" ? "Month View" : 
                 clientViewLevel === "month" ? "Year View" :
                 "Sales Team"}
              </span>
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-slate-800">{selectedClient}</span>
              {clientInfo && getRoleBadge(clientInfo.role)}
            </div>
            <span className="text-slate-400">|</span>
            <span className="text-sm text-slate-600">
              {clientViewLevel === "year" && "Year-wise Summary"}
              {clientViewLevel === "month" && `Monthly - ${selectedYear}`}
              {clientViewLevel === "date" && `Daily - ${formatMonth(selectedMonth)} ${selectedYear}`}
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Regenerate button for DP Clients */}
            {isClientRole && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRegenerateDPClientSales(selectedClient)}
                disabled={regeneratingSales}
                className="gap-1.5 text-orange-600 border-orange-300 hover:bg-orange-50"
                title="Regenerate sales with correct PRODUCT & SHIPPING rows"
              >
                <RefreshCw className={`w-4 h-4 ${regeneratingSales ? 'animate-spin' : ''}`} />
                {regeneratingSales ? "Regenerating..." : "Regenerate Sales"}
              </Button>
            )}
            
            {/* View Deleted Entries button */}
            <Button
              variant="outline"
              size="sm"
              data-testid="view-deleted-entries-btn"
              onClick={fetchDeletedEntries}
              className="gap-1.5 text-slate-600 border-slate-300 hover:bg-slate-50"
              title="View and restore deleted entries"
            >
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">Deleted</span>
            </Button>
            
            {clientInfo && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-500">Orders: <strong>{clientInfo.order_count}</strong></span>
                <span className="text-blue-600">Sales: <strong>{formatCurrency(clientInfo.total_sales)}</strong></span>
                <span className="text-emerald-600">Profit: <strong>{formatCurrency(clientInfo.total_profit)}</strong></span>
              </div>
            )}
          </div>
        </div>

        {/* Drill-down table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">#</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">
                  {clientViewLevel === "year" && "Year"}
                  {clientViewLevel === "month" && "Month"}
                  {clientViewLevel === "date" && "Tracking / Products"}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">{clientViewLevel === "date" ? "Date" : "Orders"}</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">Sell Price</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">Cost</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">Profit</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-700">Margin</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {clientDrilldownData.map((item, idx) => (
                <>
                  <tr 
                    key={item.period} 
                    className={`border-t hover:bg-slate-50 cursor-pointer ${clientViewLevel === "date" && selectedDate === item.period ? 'bg-emerald-50' : ''}`}
                    onClick={() => clientViewLevel === "date" ? handleDateClick(item.period) : handlePeriodClick(item.period)}
                  >
                    <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800 flex items-center gap-2">
                      {clientViewLevel === "date" && (
                        selectedDate === item.period 
                          ? <ChevronDown className="w-4 h-4 text-emerald-600" />
                          : <ChevronRight className="w-4 h-4" />
                      )}
                      {clientViewLevel === "year" && item.period}
                      {clientViewLevel === "month" && formatMonth(item.month)}
                      {clientViewLevel === "date" && item.day}
                    </td>
                    <td className="px-4 py-3 text-right">{item.order_count}</td>
                    <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(item.sell_price)}</td>
                    <td className="px-4 py-3 text-right text-orange-600">{formatCurrency(item.cost_price)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${item.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(item.profit)}
                    </td>
                    <td className={`px-4 py-3 text-right ${item.profit_margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {item.profit_margin.toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-center flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`delete-drilldown-group-${idx}`}
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                        title={`Delete all ${item.order_count} entries`}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Compute entries to delete based on view level
                          let salesToDelete = [];
                          if (item.sales && item.sales.length > 0) {
                            salesToDelete = item.sales;
                          } else {
                            // Filter from all drilldown data based on period
                            salesToDelete = (partnerDrilldownSales || []).filter(s => {
                              const dateStr = s.order_date || "";
                              if (dateStr.length < 6) return false;
                              const day = dateStr.substring(0,2);
                              const month = dateStr.substring(2,4);
                              const yearShort = dateStr.substring(4,6);
                              const fullYear = "20" + yearShort;
                              
                              if (clientViewLevel === "year") {
                                return fullYear === item.period;
                              } else if (clientViewLevel === "month") {
                                return `${fullYear}-${month}` === item.period;
                              } else if (clientViewLevel === "date") {
                                return `${day}-${month}-${yearShort}` === item.period;
                              }
                              return false;
                            });
                          }
                          if (salesToDelete.length > 0) {
                            initDeleteEntries(salesToDelete);
                          }
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                      {clientViewLevel === "date" 
                        ? (selectedDate === item.period 
                            ? <ChevronDown className="w-4 h-4 inline text-emerald-600" />
                            : <ChevronRight className="w-4 h-4 inline text-slate-400" />)
                        : <ChevronRight className="w-4 h-4 inline text-slate-400" />
                      }
                    </td>
                  </tr>
                  
                  {/* Expanded order rows for selected date */}
                  {clientViewLevel === "date" && selectedDate === item.period && item.sales && item.sales.map((sale, saleIdx) => (
                    <tr key={sale.id} className={`border-t border-slate-200 ${sale.row_type === 'SHIPPING' ? 'bg-blue-50' : 'bg-slate-50'}`}>
                      <td className="px-4 py-2 text-slate-400 text-xs pl-8">{saleIdx + 1}</td>
                      <td className="px-4 py-2" colSpan={1}>
                        <div className="pl-6 flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-slate-600">{sale.tracking}</span>
                            {/* Row type badge */}
                            {sale.row_type === 'SHIPPING' ? (
                              <span className="px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded font-medium">
                                SHIPPING
                              </span>
                            ) : sale.row_type === 'PRODUCT' ? (
                              <span className="px-1.5 py-0.5 text-[10px] bg-purple-100 text-purple-700 rounded font-medium">
                                PRODUCT
                              </span>
                            ) : null}
                            {sale.auto_generated && (
                              <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded font-medium" title="Auto-generated from DP Client Ledger">
                                Auto
                              </span>
                            )}
                          </div>
                          {/* Product name - show for PRODUCT rows and old data */}
                          {sale.product_name && sale.row_type !== 'SHIPPING' && (
                            <span className="text-xs text-slate-700 font-medium pl-0">
                              {sale.product_name} {sale.quantity > 1 && `×${sale.quantity}`}
                            </span>
                          )}
                          {/* Shipping info for SHIPPING rows */}
                          {sale.row_type === 'SHIPPING' && (
                            <span className="text-xs text-blue-700 font-medium pl-0">
                              {sale.service}/{sale.country} • {sale.weight?.toFixed(3)}kg
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right text-xs text-slate-500">{formatDDMMYYDisplay(sale.order_date || sale.sale_date)}</td>
                      <td className="px-4 py-2 text-right text-blue-600 text-xs">{formatCurrency(sale.sell_price)}</td>
                      <td className="px-4 py-2 text-right text-orange-600 text-xs">{formatCurrency(sale.cost_price)}</td>
                      <td className={`px-4 py-2 text-right text-xs font-medium ${sale.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(sale.profit)}
                      </td>
                      <td className={`px-4 py-2 text-right text-xs ${(sale.profit_margin || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {(sale.profit_margin || 0).toFixed(1)}%
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            data-testid={`view-sale-${saleIdx}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSale(sale);
                              setShowDetailDialog(true);
                            }}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-sale-${saleIdx}`}
                            title="Delete this entry"
                            onClick={(e) => {
                              e.stopPropagation();
                              initDeleteEntries([sale]);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
            {clientDrilldownData.length > 0 && (
              <tfoot className="bg-slate-200 font-bold">
                <tr>
                  <td colSpan={2} className="px-4 py-3 text-right">Total:</td>
                  <td className="px-4 py-3 text-right">{clientDrilldownData.reduce((s, i) => s + i.order_count, 0)}</td>
                  <td className="px-4 py-3 text-right text-blue-700">{formatCurrency(clientDrilldownData.reduce((s, i) => s + i.sell_price, 0))}</td>
                  <td className="px-4 py-3 text-right text-orange-700">{formatCurrency(clientDrilldownData.reduce((s, i) => s + i.cost_price, 0))}</td>
                  <td className="px-4 py-3 text-right text-emerald-700">{formatCurrency(clientDrilldownData.reduce((s, i) => s + i.profit, 0))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
          {clientDrilldownData.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              No data for this period
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render unmatched records table
  const renderUnmatchedTable = () => {
    return (
      <div className="space-y-4" data-testid="unmatched-records-table">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowUnmatchedTable(false)} 
              className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Summary
            </Button>
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <AlertCircle className="w-3 h-3 mr-1" />
              {unmatchedRecords.length} Unmatched Records
            </Badge>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRematchUnmatched}
              disabled={isRematching}
              className="gap-2 border-amber-400 text-amber-700 hover:bg-amber-100"
            >
              {isRematching ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Rematching...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4" />
                  Rematch All
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-amber-50 sticky top-0">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-amber-800">#</th>
                <th className="px-3 py-3 text-left font-semibold text-amber-800">Tracking</th>
                <th className="px-3 py-3 text-left font-semibold text-amber-800">Partner</th>
                <th className="px-3 py-3 text-left font-semibold text-amber-800">Customer</th>
                <th className="px-3 py-3 text-left font-semibold text-amber-800">Products</th>
                <th className="px-3 py-3 text-center font-semibold text-amber-800">Date</th>
                <th className="px-3 py-3 text-right font-semibold text-amber-800">Sell Price</th>
                <th className="px-3 py-3 text-right font-semibold text-amber-800">Cost</th>
                <th className="px-3 py-3 text-right font-semibold text-amber-800">Profit</th>
                <th className="px-3 py-3 text-center font-semibold text-amber-800">Issue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {unmatchedRecords.map((sale, idx) => (
                <tr key={sale.id || idx} className="hover:bg-amber-50/50">
                  <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                  <td className="px-3 py-2">
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">{sale.tracking || '-'}</code>
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-800">{sale.client_code || '-'}</td>
                  <td className="px-3 py-2 text-slate-600">{sale.customer_name || '-'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 max-w-[200px] truncate">
                    {sale.products?.filter(p => p.name && p.name !== 'nan').slice(0, 2).map(p => p.name).join(', ') || '-'}
                  </td>
                  <td className="px-3 py-2 text-center text-slate-600">{formatDDMMYYDisplay(sale.order_date || sale.sale_date)}</td>
                  <td className="px-3 py-2 text-right text-blue-600">{formatCurrency(sale.sell_price || 0)}</td>
                  <td className="px-3 py-2 text-right text-orange-600">
                    {sale.cost_price ? formatCurrency(sale.cost_price) : <span className="text-red-500 font-medium">₹0</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-emerald-600">{formatCurrency(sale.profit || 0)}</td>
                  <td className="px-3 py-2 text-center">
                    <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">
                      No Cost Data
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {unmatchedRecords.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300 mb-3" />
              <p className="font-medium text-emerald-600">All records are matched!</p>
              <p className="text-sm mt-1">No unmatched records found</p>
            </div>
          )}
        </div>

        {/* Summary */}
        {unmatchedRecords.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Total Unmatched Sales:</strong> {formatCurrency(unmatchedRecords.reduce((sum, s) => sum + (s.sell_price || 0), 0))}
              <span className="mx-3">|</span>
              <strong>Potential Profit Impact:</strong> These {unmatchedRecords.length} records show 100% margin because cost data is missing from Shipment Costing.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Render tab content
  const renderTabContent = (data, showRole = false, tabType = "overview") => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    
    if (sales.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 border rounded-lg bg-white">
          <FileSpreadsheet className="w-16 h-16 text-slate-300" />
          <p className="text-slate-500 font-medium mt-4">No sales records</p>
          <p className="text-sm text-slate-400 mt-1">
            {tabType === "clients" 
              ? "Generate sales from shipment orders using DP Client price lists"
              : "Upload a sales sheet or calculate from orders"}
          </p>
          <div className="flex gap-3 mt-4">
            {tabType === "clients" ? (
              <>
                <Button 
                  size="sm" 
                  onClick={handleGenerateDpClientSales} 
                  disabled={generatingDpSales}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="generate-dp-sales-empty-btn"
                >
                  {generatingDpSales ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Calculator className="w-4 h-4" />
                  )}
                  Calculate DP Client Sales
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowUploadDialog(true)} className="gap-2">
                  <Upload className="w-4 h-4" />
                  Upload Sales
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowUploadDialog(true)} className="gap-2">
                  <Upload className="w-4 h-4" />
                  Upload Sales
                </Button>
                <Button size="sm" onClick={handleCalculateFromOrders} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
                  <Zap className="w-4 h-4" />
                  Calculate from Orders
                </Button>
              </>
            )}
          </div>
        </div>
      );
    }

    // If a client is selected, show drill-down view
    if (selectedClient) {
      return renderClientDrilldown();
    }
    
    // Show search results view if active
    if (showSearchResults) {
      return renderSearchResults();
    }

    // Otherwise show client list with tab-specific actions
    return (
      <div className="space-y-4">
        {/* Search and actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant={showAdvancedSearch ? "default" : "outline"}
              size="sm"
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              className={`gap-1 sm:gap-2 whitespace-nowrap shrink-0 ${showAdvancedSearch ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              data-testid="advanced-search-toggle"
            >
              <ChevronsUpDown className="w-4 h-4" />
              <span className="hidden sm:inline">Advanced Search</span>
              <span className="sm:hidden">Adv</span>
            </Button>
          </div>
          
          <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {/* DP CLIENT-specific sales generation buttons */}
            {tabType === "clients" && (
              <>
                <select
                  value={dpSalesYear}
                  onChange={(e) => setDpSalesYear(e.target.value)}
                  disabled={generatingDpSales}
                  className="h-8 shrink-0 rounded-md border border-green-300 bg-white px-2 text-xs font-medium text-green-700 focus:outline-none"
                  title="Year of shipment orders to generate DP sales for"
                  data-testid="dp-sales-year-select"
                >
                  <option value="all">All Years</option>
                  {Array.from({ length: new Date().getFullYear() - 2022 }, (_, i) => String(new Date().getFullYear() - i)).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleGenerateDpClientSales}
                  disabled={generatingDpSales}
                  className="gap-2 border-green-300 text-green-700 hover:bg-green-50 shrink-0"
                  data-testid="generate-dp-sales-btn"
                  title={`Generate sales from ${dpSalesYear === "all" ? "ALL" : dpSalesYear} shipment orders using DP Client price lists`}
                >
                  {generatingDpSales ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Calculator className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Calculate DP Sales {dpSalesYear === "all" ? "(All)" : `(${dpSalesYear})`}</span>
                  <span className="sm:hidden">Calc DP</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleRegenerateDpClientSales()}
                  disabled={generatingDpSales}
                  className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 shrink-0"
                  data-testid="regenerate-dp-sales-btn"
                  title="Delete existing and regenerate all DP Client sales with latest prices"
                >
                  {generatingDpSales ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Regenerate All
                </Button>
              </>
            )}
            
            {/* Partner-specific upload button */}
            {tabType === "partners" && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowPartnerUploadDialog(true)}
                className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50 shrink-0"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Partner Upload</span>
                <span className="sm:hidden">Upload</span>
              </Button>
            )}
            
            {/* Agent-specific upload button */}
            {tabType === "agents" && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowAgentUploadDialog(true)}
                className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50 shrink-0"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Agent Bulk Upload</span>
                <span className="sm:hidden">Upload</span>
              </Button>
            )}
            
            {sales.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleDeleteAll}
                className="text-red-600 hover:bg-red-50 shrink-0"
              >
                <Trash2 className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Delete All</span>
                <span className="sm:hidden">Del</span>
              </Button>
            )}
          </div>
        </div>
        
        {/* Advanced Search Panel */}
        {showAdvancedSearch && (
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200" data-testid="advanced-search-panel">
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* Global Search */}
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700 font-medium">Global Search</Label>
                  <Input
                    placeholder="Search all fields..."
                    value={advancedSearchFilters.globalSearch}
                    onChange={(e) => setAdvancedSearchFilters(prev => ({...prev, globalSearch: e.target.value}))}
                    className="bg-white"
                    data-testid="search-global"
                  />
                </div>
                
                {/* Tracking Number */}
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700 font-medium">Tracking Number</Label>
                  <Input
                    placeholder="e.g., RM011..."
                    value={advancedSearchFilters.tracking}
                    onChange={(e) => setAdvancedSearchFilters(prev => ({...prev, tracking: e.target.value}))}
                    className="bg-white"
                    data-testid="search-tracking"
                  />
                </div>
                
                {/* Customer Name */}
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700 font-medium">Customer Name</Label>
                  <Input
                    placeholder="e.g., John Doe"
                    value={advancedSearchFilters.customerName}
                    onChange={(e) => setAdvancedSearchFilters(prev => ({...prev, customerName: e.target.value}))}
                    className="bg-white"
                    data-testid="search-customer"
                  />
                </div>
                
                {/* Product Name */}
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700 font-medium">Product Name</Label>
                  <Input
                    placeholder="e.g., Medicine"
                    value={advancedSearchFilters.productName}
                    onChange={(e) => setAdvancedSearchFilters(prev => ({...prev, productName: e.target.value}))}
                    className="bg-white"
                    data-testid="search-product"
                  />
                </div>
                
                {/* Date From */}
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700 font-medium">Date From</Label>
                  <DMYDateInput
  value={advancedSearchFilters.dateFrom}
                    onChange={(e) => setAdvancedSearchFilters(prev => ({...prev, dateFrom: e.target.value}))}
                    className="bg-white"
                    data-testid="search-date-from"
                  />
                </div>
                
                {/* Date To */}
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700 font-medium">Date To</Label>
                  <DMYDateInput
  value={advancedSearchFilters.dateTo}
                    onChange={(e) => setAdvancedSearchFilters(prev => ({...prev, dateTo: e.target.value}))}
                    className="bg-white"
                    data-testid="search-date-to"
                  />
                </div>
              </div>
              
              {/* Search Actions */}
              <div className="flex items-center justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAdvancedSearch}
                  className="gap-2"
                  data-testid="search-clear"
                >
                  <X className="w-4 h-4" />
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={() => performAdvancedSearch(1)}
                  disabled={isSearching}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  data-testid="search-submit"
                >
                  {isSearching ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Search
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {renderClientList(data, showRole)}
      </div>
    );
  };

  // Render Ledger Reconciliation Tab
  const renderLedgerReconciliation = () => {
    if (reconciliationLoading && !reconciliationData) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    // Back navigation for details view
    const handleBackToSummary = () => {
      setReconciliationViewLevel("summary");
      setReconciliationSelectedCode(null);
      setReconciliationDetails(null);
    };

    // Render client details view
    if (reconciliationViewLevel === "details" && reconciliationDetails) {
      const { transactions, tracking_verification, summary: detailSummary } = reconciliationDetails;
      
      return (
        <div className="space-y-4">
          {/* Header with back button */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleBackToSummary}
              className="gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
            >
              <ArrowLeft className="w-4 h-4" />
              ← Sales Team
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setManualEntryType("take");
                  setShowManualEntryDialog(true);
                }}
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Plus className="w-4 h-4" />
                Add Debit (Take)
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setManualEntryType("give");
                  setShowManualEntryDialog(true);
                }}
                className="gap-2 text-green-600 border-green-200 hover:bg-green-50"
              >
                <Plus className="w-4 h-4" />
                Add Credit (Give)
              </Button>
            </div>
          </div>

          {/* Client Code Title */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-800">{reconciliationSelectedCode}</h2>
            <p className="text-slate-500">Ledger Reconciliation Details</p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="pt-4 text-center">
                <p className="text-xs text-red-600 font-medium">Total Debit</p>
                <p className="text-xl font-bold text-red-700">₹{detailSummary?.total_debit?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="pt-4 text-center">
                <p className="text-xs text-green-600 font-medium">Total Credit</p>
                <p className="text-xl font-bold text-green-700">₹{detailSummary?.total_credit?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-4 text-center">
                <p className="text-xs text-blue-600 font-medium">Balance</p>
                <p className={`text-xl font-bold ${detailSummary?.balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                  ₹{detailSummary?.balance?.toLocaleString()}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="pt-4 text-center">
                <p className="text-xs text-amber-600 font-medium">Tracking Errors</p>
                <p className={`text-xl font-bold ${detailSummary?.error_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {detailSummary?.error_count || 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs for Transactions and Tracking Verification */}
          <Tabs defaultValue="transactions" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="transactions">Transactions ({transactions?.length || 0})</TabsTrigger>
              <TabsTrigger value="tracking">Tracking Verification ({tracking_verification?.length || 0})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="transactions" className="mt-4">
              <Card>
                <CardContent className="p-0">
                  <div className="max-h-[400px] overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="p-2 text-left font-semibold">Date</th>
                          <th className="p-2 text-left font-semibold">Type</th>
                          <th className="p-2 text-left font-semibold">Category</th>
                          <th className="p-2 text-left font-semibold">Description</th>
                          <th className="p-2 text-left font-semibold">Tracking</th>
                          <th className="p-2 text-right font-semibold">Amount</th>
                          <th className="p-2 text-center font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions?.map((tx, idx) => (
                          <tr key={tx.id || idx} className="border-b hover:bg-slate-50">
                            <td className="p-2">{formatDMY(tx.date)}</td>
                            <td className="p-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                tx.type === "debit" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                              }`}>
                                {tx.type === "debit" ? "DEBIT" : "CREDIT"}
                              </span>
                            </td>
                            <td className="p-2 capitalize">{tx.category}</td>
                            <td className="p-2 text-slate-600">{tx.description}</td>
                            <td className="p-2 font-mono text-xs">{tx.tracking || "-"}</td>
                            <td className={`p-2 text-right font-semibold ${
                              tx.type === "debit" ? "text-red-600" : "text-green-600"
                            }`}>
                              {tx.type === "debit" ? "-" : "+"}₹{tx.amount?.toLocaleString()}
                            </td>
                            <td className="p-2 text-center">
                              {tx.status === "pending" && (
                                <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-700">Pending</span>
                              )}
                              {tx.status === "approved" && (
                                <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">Approved</span>
                              )}
                              {!tx.status && <span className="text-slate-400">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="tracking" className="mt-4">
              <Card>
                <CardContent className="p-0">
                  <div className="max-h-[400px] overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="p-2 text-left font-semibold">Tracking</th>
                          <th className="p-2 text-left font-semibold">Date</th>
                          <th className="p-2 text-right font-semibold">Costing</th>
                          <th className="p-2 text-right font-semibold">Sell Price</th>
                          <th className="p-2 text-right font-semibold">GP</th>
                          <th className="p-2 text-right font-semibold">GP Self</th>
                          <th className="p-2 text-right font-semibold">Payment</th>
                          <th className="p-2 text-center font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tracking_verification?.map((track, idx) => (
                          <tr key={track.tracking || idx} className={`border-b hover:bg-slate-50 ${
                            track.status === "error" ? "bg-red-50" : ""
                          }`}>
                            <td className="p-2 font-mono text-xs">{track.tracking}</td>
                            <td className="p-2">{formatDMY(track.date)}</td>
                            <td className="p-2 text-right text-slate-600">₹{track.costing?.toLocaleString()}</td>
                            <td className="p-2 text-right text-slate-800">₹{track.sell_price?.toLocaleString()}</td>
                            <td className={`p-2 text-right font-semibold ${
                              track.gp < 0 ? "text-red-600" : "text-green-600"
                            }`}>
                              ₹{track.gp?.toLocaleString()}
                            </td>
                            <td className="p-2 text-right text-blue-600">₹{track.gp_self?.toLocaleString()}</td>
                            <td className="p-2 text-right text-green-600">₹{track.payment_received?.toLocaleString()}</td>
                            <td className="p-2 text-center">
                              {track.status === "error" && (
                                <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-700 flex items-center justify-center gap-1">
                                  <AlertTriangle className="w-3 h-3" /> Error
                                </span>
                              )}
                              {track.status === "settled" && (
                                <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">Settled</span>
                              )}
                              {track.status === "pending" && (
                                <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-700">Pending</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      );
    }

    // Render main summary view
    const clients = reconciliationData?.clients || [];
    const summaryData = reconciliationData?.summary || {};
    
    // Filter by search
    const filteredClients = clients.filter(c => 
      c.client_code?.toLowerCase().includes(reconciliationSearch.toLowerCase())
    );

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            Ledger Reconciliation
          </h2>
          {pendingApprovals.length > 0 && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="w-3 h-3" />
              {pendingApprovals.length} Pending Approvals
            </Badge>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-red-600 font-medium">Total Debit</p>
              <p className="text-xl font-bold text-red-700">₹{summaryData?.total_debit?.toLocaleString()}</p>
              <p className="text-xs text-red-500">Costing + GP Self</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-green-600 font-medium">Total Credit</p>
              <p className="text-xl font-bold text-green-700">₹{summaryData?.total_credit?.toLocaleString()}</p>
              <p className="text-xs text-green-500">Payments Received</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-orange-600 font-medium">Total Balance</p>
              <p className="text-xl font-bold text-orange-700">₹{summaryData?.total_balance?.toLocaleString()}</p>
              <p className="text-xs text-orange-500">To Collect</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-blue-600 font-medium">Total Costing</p>
              <p className="text-xl font-bold text-blue-700">₹{summaryData?.total_costing?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-purple-600 font-medium">GP Self</p>
              <p className="text-xl font-bold text-purple-700">₹{summaryData?.total_gp_self?.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search client code..."
              value={reconciliationSearch}
              onChange={(e) => setReconciliationSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-2 items-center">
            <Calendar className="w-4 h-4 text-slate-400" />
            <DMYDateInput placeholder="From" value={reconciliationDateFrom} onChange={(e) => setReconciliationDateFrom(e.target.value)} className="w-40" />
            <span className="text-slate-400">to</span>
            <DMYDateInput placeholder="To" value={reconciliationDateTo} onChange={(e) => setReconciliationDateTo(e.target.value)} className="w-40" />
          </div>
          <Button
            variant="outline"
            onClick={fetchReconciliationData}
            disabled={reconciliationLoading}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${reconciliationLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Client Table */}
        <Card>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 text-left font-semibold">Client Code</th>
                    <th className="p-3 text-right font-semibold">Orders</th>
                    <th className="p-3 text-right font-semibold">Sales</th>
                    <th className="p-3 text-right font-semibold text-slate-600">Costing</th>
                    <th className="p-3 text-right font-semibold text-purple-600">GP Self</th>
                    <th className="p-3 text-right font-semibold text-red-600">Total Debit</th>
                    <th className="p-3 text-right font-semibold text-green-600">Total Credit</th>
                    <th className="p-3 text-right font-semibold text-orange-600">Balance</th>
                    <th className="p-3 text-center font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client, idx) => (
                    <tr key={client.client_code || idx} className="border-b hover:bg-slate-50">
                      <td className="p-3 font-semibold text-blue-600">{client.client_code}</td>
                      <td className="p-3 text-right">{client.order_count}</td>
                      <td className="p-3 text-right">{client.sales_count}</td>
                      <td className="p-3 text-right text-slate-600">₹{client.total_costing?.toLocaleString()}</td>
                      <td className="p-3 text-right text-purple-600">₹{client.total_gp_self?.toLocaleString()}</td>
                      <td className="p-3 text-right font-semibold text-red-600">₹{client.total_debit?.toLocaleString()}</td>
                      <td className="p-3 text-right font-semibold text-green-600">₹{client.total_credit?.toLocaleString()}</td>
                      <td className={`p-3 text-right font-bold ${
                        client.balance > 0 ? 'text-orange-600' : client.balance < 0 ? 'text-green-600' : 'text-slate-500'
                      }`}>
                        ₹{client.balance?.toLocaleString()}
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchReconciliationDetails(client.client_code)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          View Details →
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Stats Footer */}
        <div className="text-sm text-slate-500 text-center">
          Showing {filteredClients.length} of {clients.length} clients • 
          {summaryData?.pending_collection_count} pending collection
        </div>
      </div>
    );
  };

  // Render GP Distribution Tab
  const renderGpDistribution = () => {
    if (gpLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (!gpData) {
      return (
        <div className="text-center py-12 text-slate-500">
          No GP distribution data available
        </div>
      );
    }

    // Helper to get role for a code
    const getCodeRole = (code) => {
      const ledger = ledgers.find(l => l.client_code?.toUpperCase() === code?.toUpperCase());
      return ledger?.role || "Client";
    };

    // Role badge component
    const RoleBadge = ({ role }) => {
      const styles = {
        "Agent": "bg-purple-100 text-purple-700 border-purple-200",
        "Partner": "bg-blue-100 text-blue-700 border-blue-200",
        "Client": "bg-green-100 text-green-600 border-green-200",
        "Owner": "bg-emerald-100 text-emerald-700 border-emerald-200"
      };
      const displayName = role === "Client" ? "DP CLIENT" : role;
      return (
        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${styles[role] || styles.Client}`}>
          {displayName}
        </span>
      );
    };

    // GP Sort handler
    const handleGpSort = (key) => {
      setGpSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
      }));
    };

    // Sort icon component
    const SortIcon = ({ columnKey }) => {
      if (gpSortConfig.key !== columnKey) {
        return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
      }
      return gpSortConfig.direction === "desc" 
        ? <ArrowDown className="w-3 h-3 ml-1 text-emerald-600" />
        : <ArrowUp className="w-3 h-3 ml-1 text-emerald-600" />;
    };

    // Summary view (GP share totals by recipient)
    if (gpViewLevel === "summary") {
      const gpByCode = gpData.gp_by_code || [];
      const gpSharesTotals = gpData.gp_shares_totals || [];
      
      // Filter and sort GP data
      const filteredGpByCode = gpByCode
        .filter(item => {
          // Search filter
          if (gpSearchTerm && !item.client_code?.toLowerCase().includes(gpSearchTerm.toLowerCase())) {
            return false;
          }
          // Role filter
          if (gpRoleFilter !== "all") {
            const role = getCodeRole(item.client_code);
            if (role !== gpRoleFilter) return false;
          }
          // Only show items with profit
          return item.total_profit > 0;
        })
        .sort((a, b) => {
          let aVal, bVal;
          switch (gpSortConfig.key) {
            case "client_code":
              aVal = a.client_code || "";
              bVal = b.client_code || "";
              return gpSortConfig.direction === "desc" 
                ? bVal.localeCompare(aVal) 
                : aVal.localeCompare(bVal);
            case "order_count":
              aVal = a.order_count || 0;
              bVal = b.order_count || 0;
              break;
            case "total_profit":
              aVal = a.total_profit || 0;
              bVal = b.total_profit || 0;
              break;
            case "self_gp":
              aVal = a.self_gp?.amount || 0;
              bVal = b.self_gp?.amount || 0;
              break;
            case "partner1_gp":
              aVal = a.partner1_gp?.amount || 0;
              bVal = b.partner1_gp?.amount || 0;
              break;
            case "partner2_gp":
              aVal = a.partner2_gp?.amount || 0;
              bVal = b.partner2_gp?.amount || 0;
              break;
            case "role":
              aVal = getCodeRole(a.client_code);
              bVal = getCodeRole(b.client_code);
              return gpSortConfig.direction === "desc" 
                ? bVal.localeCompare(aVal) 
                : aVal.localeCompare(bVal);
            default:
              aVal = a.total_profit || 0;
              bVal = b.total_profit || 0;
          }
          return gpSortConfig.direction === "desc" ? bVal - aVal : aVal - bVal;
        });
      
      return (
        <div className="space-y-6">
          {/* GP Share Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {gpSharesTotals.slice(0, 4).map((share, idx) => (
              <div 
                key={share.name} 
                className={`bg-white rounded-xl border p-4 shadow-sm ${
                  share.name.toUpperCase() === "SELF" ? "border-emerald-200 bg-emerald-50" : ""
                }`}
              >
                <div className="flex items-center gap-2 text-slate-600">
                  {share.name.toUpperCase() === "SELF" ? (
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Users className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">{share.name}</span>
                </div>
                <p className={`text-xl font-bold mt-2 ${
                  share.name.toUpperCase() === "SELF" ? "text-emerald-700" : "text-slate-800"
                }`}>
                  {formatCurrency(share.total_amount)}
                </p>
                <p className="text-xs text-slate-500">{share.codes_count} codes</p>
              </div>
            ))}
          </div>

          {/* Search and Filter Bar */}
          <div className="flex items-center gap-4 bg-white p-4 rounded-lg border">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by client code..."
                value={gpSearchTerm}
                onChange={(e) => setGpSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={gpRoleFilter} onValueChange={setGpRoleFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="Client">DP CLIENTS</SelectItem>
                <SelectItem value="Partner">Partner</SelectItem>
                <SelectItem value="Agent">Agent</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-slate-500">
              {filteredGpByCode.length} of {gpByCode.filter(i => i.total_profit > 0).length} codes
            </div>
          </div>

          {/* Code-wise GP Table with Separate Columns */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-emerald-600" />
                Code-wise GP Distribution
              </h3>
              <span className="text-sm text-slate-500">
                Total Profit: {formatCurrency(gpData.summary?.total_profit || 0)}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700 w-12">#</th>
                    <th 
                      className="px-3 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleGpSort("client_code")}
                    >
                      <div className="flex items-center">
                        Client Code
                        <SortIcon columnKey="client_code" />
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleGpSort("role")}
                    >
                      <div className="flex items-center">
                        Role
                        <SortIcon columnKey="role" />
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-right font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleGpSort("order_count")}
                    >
                      <div className="flex items-center justify-end">
                        Orders
                        <SortIcon columnKey="order_count" />
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-right font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleGpSort("total_profit")}
                    >
                      <div className="flex items-center justify-end">
                        Total Profit
                        <SortIcon columnKey="total_profit" />
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-right font-semibold text-emerald-700 bg-emerald-50 cursor-pointer hover:bg-emerald-100 select-none"
                      onClick={() => handleGpSort("self_gp")}
                    >
                      <div className="flex items-center justify-end">
                        Self GP
                        <SortIcon columnKey="self_gp" />
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-right font-semibold text-blue-700 bg-blue-50 cursor-pointer hover:bg-blue-100 select-none"
                      onClick={() => handleGpSort("partner1_gp")}
                    >
                      <div className="flex items-center justify-end">
                        Partner 1 GP
                        <SortIcon columnKey="partner1_gp" />
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-right font-semibold text-purple-700 bg-purple-50 cursor-pointer hover:bg-purple-100 select-none"
                      onClick={() => handleGpSort("partner2_gp")}
                    >
                      <div className="flex items-center justify-end">
                        Partner 2 GP
                        <SortIcon columnKey="partner2_gp" />
                      </div>
                    </th>
                    <th className="px-3 py-3 text-center font-semibold text-slate-700 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGpByCode.map((item, idx) => {
                      const selfGp = item.self_gp || {};
                      const partner1Gp = item.partner1_gp || {};
                      const partner2Gp = item.partner2_gp || {};
                      const role = getCodeRole(item.client_code);
                      
                      return (
                        <tr 
                          key={item.client_code} 
                          className="border-t hover:bg-slate-50 cursor-pointer"
                          onClick={() => gpSelectCode(item.client_code)}
                        >
                          <td className="px-3 py-3 text-slate-500">{idx + 1}</td>
                          <td className="px-3 py-3 font-semibold text-slate-800">{item.client_code}</td>
                          <td className="px-3 py-3">
                            <RoleBadge role={role} />
                          </td>
                          <td className="px-3 py-3 text-right">{item.order_count}</td>
                          <td className="px-3 py-3 text-right font-semibold text-slate-800">
                            {formatCurrency(item.total_profit)}
                          </td>
                          <td className="px-3 py-3 text-right bg-emerald-50/50">
                            {selfGp.amount > 0 ? (
                              <div>
                                <span className="font-semibold text-emerald-700">
                                  {formatCurrency(selfGp.amount)}
                                </span>
                                <span className="text-xs text-emerald-600 ml-1">
                                  ({selfGp.percentage}%)
                                </span>
                              </div>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right bg-blue-50/50">
                            {partner1Gp.amount > 0 ? (
                              <div>
                                <span className="font-semibold text-blue-700">
                                  {formatCurrency(partner1Gp.amount)}
                                </span>
                                <span className="text-xs text-blue-600 ml-1">
                                  ({partner1Gp.percentage}%)
                                </span>
                                <div className="text-xs text-blue-500 font-medium">
                                  {partner1Gp.name}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right bg-purple-50/50">
                            {partner2Gp.amount > 0 ? (
                              <div>
                                <span className="font-semibold text-purple-700">
                                  {formatCurrency(partner2Gp.amount)}
                                </span>
                                <span className="text-xs text-purple-600 ml-1">
                                  ({partner2Gp.percentage}%)
                                </span>
                                <div className="text-xs text-purple-500 font-medium">
                                  {partner2Gp.name}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <ChevronRight className="w-4 h-4 inline text-slate-400" />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    // Code detail view with drill-down
    if (gpViewLevel === "code" && gpCodeData) {
      const { summary: codeSummary, gp_totals, profit_ratios, orders_by_date } = gpCodeData;
      const ordersGroupedByYear = {};
      
      // Group orders by year
      (orders_by_date || []).forEach(dateItem => {
        const year = dateItem.date?.substring(0, 4) || "Unknown";
        if (!ordersGroupedByYear[year]) {
          ordersGroupedByYear[year] = { year, total_profit: 0, order_count: 0, months: {} };
        }
        ordersGroupedByYear[year].total_profit += dateItem.total_profit || 0;
        ordersGroupedByYear[year].order_count += (dateItem.orders?.length || 0);
        
        // Group by month
        const month = dateItem.date?.substring(0, 7) || "Unknown";
        if (!ordersGroupedByYear[year].months[month]) {
          ordersGroupedByYear[year].months[month] = { month, total_profit: 0, order_count: 0, dates: [] };
        }
        ordersGroupedByYear[year].months[month].total_profit += dateItem.total_profit || 0;
        ordersGroupedByYear[year].months[month].order_count += (dateItem.orders?.length || 0);
        ordersGroupedByYear[year].months[month].dates.push(dateItem);
      });

      return (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={gpGoBack} className="gap-1 border-slate-300 hover:bg-slate-100">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">
                  {gpViewLevel === "month" ? "Month View" : 
                   gpViewLevel === "year" ? "Year View" :
                   "All Codes"}
                </span>
              </Button>
              <span className="text-lg font-bold text-slate-800">{gpSelectedCode}</span>
              <span className="text-slate-400">|</span>
              <span className="text-sm text-slate-600">
                {gpViewLevel === "code" && "Year-wise Summary"}
                {gpViewLevel === "year" && `Monthly - ${gpSelectedYear}`}
                {gpViewLevel === "month" && `Daily - ${gpSelectedMonth}`}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-600">Total Profit: <strong className="text-emerald-600">{formatCurrency(codeSummary?.total_profit || 0)}</strong></span>
            </div>
          </div>

          {/* GP Share breakdown card - Fixed percentage display */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-4">
            <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-emerald-600" />
              GP Distribution for {gpSelectedCode}
            </h4>
            <div className="flex flex-wrap gap-3">
              {(gp_totals || []).map((gp, idx) => (
                <div 
                  key={idx} 
                  className={`px-4 py-3 rounded-lg ${
                    gp.share_type === "self" 
                      ? "bg-emerald-100 border border-emerald-300" 
                      : gp.share_type === "partner1"
                        ? "bg-blue-100 border border-blue-300"
                        : "bg-purple-100 border border-purple-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                      gp.share_type === "self" ? "bg-emerald-200 text-emerald-800" :
                      gp.share_type === "partner1" ? "bg-blue-200 text-blue-800" :
                      "bg-purple-200 text-purple-800"
                    }`}>
                      {gp.percentage}%
                    </span>
                    <span className="text-sm text-slate-600">{gp.name}</span>
                  </div>
                  <p className={`text-xl font-bold mt-1 ${
                    gp.share_type === "self" ? "text-emerald-700" : 
                    gp.share_type === "partner1" ? "text-blue-700" :
                    "text-purple-700"
                  }`}>
                    {formatCurrency(gp.amount)}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Ratio: Self {profit_ratios?.ratio1 || 0}% | Partner1 {profit_ratios?.ratio2 || 0}% | Partner2 {profit_ratios?.ratio3 || 0}%
            </div>
          </div>

          {/* Year/Month/Date drill-down table with columns */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">#</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">
                      {gpViewLevel === "code" && "Year"}
                      {gpViewLevel === "year" && "Month"}
                      {gpViewLevel === "month" && "Date"}
                    </th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Orders</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Profit</th>
                    <th className="px-4 py-3 text-right font-semibold text-emerald-700 bg-emerald-50">Self GP ({profit_ratios?.ratio1 || 0}%)</th>
                    {profit_ratios?.ratio2 > 0 && (
                      <th className="px-4 py-3 text-right font-semibold text-blue-700 bg-blue-50">
                        {profit_ratios?.share2_name || profit_ratios?.under || "Partner 1"} ({profit_ratios?.ratio2}%)
                      </th>
                    )}
                    {profit_ratios?.ratio3 > 0 && (
                      <th className="px-4 py-3 text-right font-semibold text-purple-700 bg-purple-50">
                        {profit_ratios?.share3_name || "Partner 2"} ({profit_ratios?.ratio3}%)
                      </th>
                    )}
                    <th className="px-4 py-3 text-center font-semibold text-slate-700"></th>
                  </tr>
                </thead>
                <tbody>
                {gpViewLevel === "code" && Object.values(ordersGroupedByYear)
                  .sort((a, b) => b.year.localeCompare(a.year))
                  .map((yearItem, idx) => {
                    const selfGpAmount = (profit_ratios?.ratio1 / 100) * yearItem.total_profit;
                    const partner1GpAmount = (profit_ratios?.ratio2 / 100) * yearItem.total_profit;
                    const partner2GpAmount = (profit_ratios?.ratio3 / 100) * yearItem.total_profit;
                    
                    return (
                      <tr 
                        key={yearItem.year} 
                        className="border-t hover:bg-slate-50 cursor-pointer"
                        onClick={() => gpSelectYear(yearItem.year)}
                      >
                        <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                        <td className="px-4 py-3 font-semibold text-slate-800">{yearItem.year}</td>
                        <td className="px-4 py-3 text-right">{yearItem.order_count}</td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-800">
                          {formatCurrency(yearItem.total_profit)}
                        </td>
                        <td className="px-4 py-3 text-right bg-emerald-50/50 font-semibold text-emerald-700">
                          {formatCurrency(selfGpAmount)}
                        </td>
                        {profit_ratios?.ratio2 > 0 && (
                          <td className="px-4 py-3 text-right bg-blue-50/50 font-semibold text-blue-700">
                            {formatCurrency(partner1GpAmount)}
                          </td>
                        )}
                        {profit_ratios?.ratio3 > 0 && (
                          <td className="px-4 py-3 text-right bg-purple-50/50 font-semibold text-purple-700">
                            {formatCurrency(partner2GpAmount)}
                          </td>
                        )}
                        <td className="px-4 py-3 text-center">
                          <ChevronRight className="w-4 h-4 inline text-slate-400" />
                        </td>
                      </tr>
                    );
                  })}
                
                {gpViewLevel === "year" && gpSelectedYear && ordersGroupedByYear[gpSelectedYear] && 
                  Object.values(ordersGroupedByYear[gpSelectedYear].months)
                    .sort((a, b) => b.month.localeCompare(a.month))
                    .map((monthItem, idx) => {
                      const selfGpAmount = (profit_ratios?.ratio1 / 100) * monthItem.total_profit;
                      const partner1GpAmount = (profit_ratios?.ratio2 / 100) * monthItem.total_profit;
                      const partner2GpAmount = (profit_ratios?.ratio3 / 100) * monthItem.total_profit;
                      
                      return (
                        <tr 
                          key={monthItem.month} 
                          className="border-t hover:bg-slate-50 cursor-pointer"
                          onClick={() => gpSelectMonth(monthItem.month.substring(5))}
                        >
                          <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                          <td className="px-4 py-3 font-semibold text-slate-800">
                            {formatMonth(monthItem.month)}
                          </td>
                          <td className="px-4 py-3 text-right">{monthItem.order_count}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-800">
                            {formatCurrency(monthItem.total_profit)}
                          </td>
                          <td className="px-4 py-3 text-right bg-emerald-50/50 font-semibold text-emerald-700">
                            {formatCurrency(selfGpAmount)}
                          </td>
                          {profit_ratios?.ratio2 > 0 && (
                            <td className="px-4 py-3 text-right bg-blue-50/50 font-semibold text-blue-700">
                              {formatCurrency(partner1GpAmount)}
                            </td>
                          )}
                          {profit_ratios?.ratio3 > 0 && (
                            <td className="px-4 py-3 text-right bg-purple-50/50 font-semibold text-purple-700">
                              {formatCurrency(partner2GpAmount)}
                            </td>
                          )}
                          <td className="px-4 py-3 text-center">
                            <ChevronRight className="w-4 h-4 inline text-slate-400" />
                          </td>
                        </tr>
                      );
                    })}
                
                {gpViewLevel === "month" && gpSelectedYear && gpSelectedMonth && 
                  ordersGroupedByYear[gpSelectedYear]?.months[`${gpSelectedYear}-${gpSelectedMonth}`]?.dates
                    .sort((a, b) => b.date.localeCompare(a.date))
                    .map((dateItem, idx) => {
                      const selfGpAmount = (profit_ratios?.ratio1 / 100) * dateItem.total_profit;
                      const partner1GpAmount = (profit_ratios?.ratio2 / 100) * dateItem.total_profit;
                      const partner2GpAmount = (profit_ratios?.ratio3 / 100) * dateItem.total_profit;
                      
                      return (
                        <tr key={formatDMY(dateItem.date)} className="border-t hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                          <td className="px-4 py-3 font-semibold text-slate-800">{formatDMY(dateItem.date)}</td>
                          <td className="px-4 py-3 text-right">{dateItem.orders?.length || 0}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-800">
                            {formatCurrency(dateItem.total_profit)}
                          </td>
                          <td className="px-4 py-3 text-right bg-emerald-50/50 font-semibold text-emerald-700">
                            {formatCurrency(selfGpAmount)}
                          </td>
                          {profit_ratios?.ratio2 > 0 && (
                            <td className="px-4 py-3 text-right bg-blue-50/50 font-semibold text-blue-700">
                              {formatCurrency(partner1GpAmount)}
                            </td>
                          )}
                          {profit_ratios?.ratio3 > 0 && (
                            <td className="px-4 py-3 text-right bg-purple-50/50 font-semibold text-purple-700">
                              {formatCurrency(partner2GpAmount)}
                            </td>
                          )}
                          <td className="px-4 py-3"></td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Sales & Profit</h1>
            <p className="text-sm text-slate-500 flex items-center gap-2">
              {sales.length} records | Profit: {formatCurrency(summary.total_profit || 0)}
              {loadTime !== null && !loading && (
                <Badge 
                  variant="outline" 
                  className={`text-xs ${loadTime < 200 ? 'border-green-400 text-green-600 bg-green-50' : loadTime < 500 ? 'border-yellow-400 text-yellow-600 bg-yellow-50' : 'border-red-400 text-red-600 bg-red-50'}`}
                  data-testid="load-time-badge"
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {loadTime}ms
                </Badge>
              )}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
          <Button variant="outline" size="sm" onClick={() => { fetchSales(); backToList(); }} className="gap-2 shrink-0">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowUploadDialog(true)} className="gap-2 shrink-0">
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Upload Sales</span>
            <span className="sm:hidden">Upload</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowRawPartnerUploadDialog(true)} className="gap-2 border-purple-300 text-purple-600 hover:bg-purple-50 shrink-0">
            <FileSpreadsheet className="w-4 h-4" />
            <span className="hidden sm:inline">Upload Partner Sheets</span>
            <span className="sm:hidden">Partner</span>
          </Button>
          <Button 
            size="sm" 
            onClick={handleCalculateFromOrders} 
            disabled={calculating}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 shrink-0"
          >
            <Zap className="w-4 h-4" />
            <span className="hidden sm:inline">{calculating 
              ? calcProgress.total > 0 
                ? `Calculating... ${calcProgress.current.toLocaleString()}/${calcProgress.total.toLocaleString()}`
                : "Calculating..."
              : "Calculate from Orders"
            }</span>
            <span className="sm:hidden">{calculating ? "Calc..." : "Calculate"}</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={generateMissingSales}
            disabled={generatingMissing}
            className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{generatingMissing ? "Generating..." : "Sync Missing"}</span>
            <span className="sm:hidden">{generatingMissing ? "Sync..." : "Sync"}</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={cleanupDuplicateSales}
            disabled={cleaningDuplicates}
            className="gap-2 border-red-300 text-red-700 hover:bg-red-50 shrink-0"
            title="Remove duplicate placeholder records where real sales records exist"
            data-testid="cleanup-duplicates-btn"
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">{cleaningDuplicates ? "Cleaning..." : "Cleanup Duplicates"}</span>
            <span className="sm:hidden">{cleaningDuplicates ? "Clean..." : "Cleanup"}</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={resetPartnerAgentSellPrices}
            disabled={resettingPartnerSellPrices}
            className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50 shrink-0"
            title="Reset incorrect auto-calculated sell prices for Partner/Agent records"
            data-testid="reset-partner-sell-prices-btn"
          >
            <RefreshCw className={`w-4 h-4 ${resettingPartnerSellPrices ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{resettingPartnerSellPrices ? "Resetting..." : "Fix Partner Sell Prices"}</span>
            <span className="sm:hidden">{resettingPartnerSellPrices ? "Fix..." : "Fix Prices"}</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
        {/* Parcels Card - Shows unique trackings matching shipment costing */}
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500">
            <Package className="w-4 h-4" />
            <span className="text-sm">Parcels</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-xs">
                    <strong>Unique tracking numbers</strong> in Sales panel.
                    Should match Shipment Costing parcels count.
                    <br/><br/>
                    <span className="text-green-600">✓ {summary.parcels_coverage || 0}% coverage</span> means 
                    {summary.parcels_coverage >= 100 ? " all" : " most"} parcels have sales records.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            <p className="text-2xl font-bold text-slate-800">{(summary.unique_parcels || 0).toLocaleString()}</p>
            <span className="text-sm text-slate-400">/ {(summary.total_shipment_parcels || 0).toLocaleString()}</span>
          </div>
          {summary.parcels_coverage !== undefined && (
            <div className="mt-1 flex items-center gap-1">
              {summary.parcels_coverage >= 100 ? (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> 100% synced
                </span>
              ) : (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {summary.parcels_coverage}% coverage
                </span>
              )}
            </div>
          )}
        </div>

        {/* Warning when coverage is low */}
        {summary.parcels_coverage !== undefined && summary.parcels_coverage < 100 && (
          <div className="col-span-2 lg:col-span-6 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="text-sm text-amber-800">
                  <strong>{summary.total_shipment_parcels - summary.unique_parcels} orders</strong> from Ledger don't have sales records yet. 
                  Click <strong>"Sync Missing"</strong> to create placeholder entries.
                </span>
              </div>
              <Button
                size="sm"
                onClick={generateMissingSales}
                disabled={generatingMissing}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {generatingMissing ? "Syncing..." : "Sync Now"}
              </Button>
            </div>
            {/* Progress bar when syncing */}
            {syncProgress.isRunning && syncProgress.total > 0 && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-amber-700 mb-1">
                  <span>Syncing records...</span>
                  <span>{syncProgress.current.toLocaleString()} / {syncProgress.total.toLocaleString()}</span>
                </div>
                <div className="w-full bg-amber-200 rounded-full h-2">
                  <div 
                    className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.round((syncProgress.current / syncProgress.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sales Rows Card */}
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500">
            <FileText className="w-4 h-4" />
            <span className="text-sm">Sales Rows</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-xs">
                    Total individual sales line items.
                    Each parcel can have multiple rows (products + shipping).
                    <br/><br/>
                    Example: 1 parcel with 3 products = 4 rows
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-slate-600 mt-2">{(summary.total_records || 0).toLocaleString()}</p>
        </div>
        
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-blue-500">
            <IndianRupee className="w-4 h-4" />
            <span className="text-sm">Total Sales</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-blue-600 mt-2 break-all leading-tight">{formatCurrency(summary.total_sales || 0)}</p>
        </div>
        
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-orange-500">
            <Wallet className="w-4 h-4" />
            <span className="text-sm">Total Cost</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-orange-600 mt-2 break-all leading-tight">{formatCurrency(summary.total_cost || 0)}</p>
        </div>
        
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-emerald-500">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Total Profit</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-emerald-600 mt-2 break-all leading-tight">{formatCurrency(summary.total_profit || 0)}</p>
        </div>
        
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <div className="flex items-center gap-2 text-purple-500">
            <Target className="w-4 h-4" />
            <span className="text-sm">Profit Margin</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-purple-600 mt-2">{summary.profit_margin || 0}%</p>
        </div>
      </div>

      {/* Status Filter Bar */}
      {statusCounts.unmatched > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:bg-amber-100 rounded-lg px-2 py-1 -mx-2 -my-1 transition-colors flex-1 overflow-x-auto"
            onClick={() => {
              setActiveTab("overview");
              setShowUnmatchedTable(true);
              setSelectedClient(null);
            }}
            data-testid="unmatched-records-click"
          >
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                {statusCounts.unmatched} Unmatched Records
                <Eye className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-amber-500 font-normal">(Click to view)</span>
              </p>
              <p className="text-xs text-amber-600">
                These records don&apos;t have cost data from Shipment Costing (GP cannot be calculated accurately)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setActiveTab("overview");
                setShowUnmatchedTable(true);
                setSelectedClient(null);
              }}
              className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-100"
              data-testid="view-unmatched-btn"
            >
              <Eye className="w-4 h-4" />
              View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRematchUnmatched}
              disabled={isRematching}
              className="gap-2 border-amber-400 text-amber-700 hover:bg-amber-100"
              data-testid="rematch-btn"
            >
              {isRematching ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Rematching...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4" />
                  Rematch
                </>
              )}
            </Button>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setShowUnmatchedTable(false);
              }}
              className="text-sm border border-amber-300 rounded-md px-2 py-1 bg-white cursor-pointer"
              data-testid="filter-status-dropdown"
            >
              <option value="all">Show All ({statusCounts.total})</option>
              <option value="matched">Matched Only ({statusCounts.matched})</option>
              <option value="unmatched">Unmatched Only ({statusCounts.unmatched})</option>
            </select>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); backToList(); gpBackToSummary(); }} className="w-full">
        <TabsList className="flex w-full max-w-4xl overflow-x-auto no-scrollbar">
          <TabsTrigger value="overview" className="gap-1 sm:gap-2 shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <LayoutGrid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Overview</span> ({filteredAllClientsSummary.length}{filterStatus !== "all" ? `/${allClientsSummary.length}` : ""})
          </TabsTrigger>
          <TabsTrigger value="clients" className="gap-1 sm:gap-2 shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">By</span> DP ({filteredClientSummary.length}{filterStatus !== "all" ? `/${clientSummary.length}` : ""})
          </TabsTrigger>
          <TabsTrigger value="partners" className="gap-1 sm:gap-2 shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <Handshake className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">By</span> Partner ({filteredPartnerSummary.length}{filterStatus !== "all" ? `/${partnerSummary.length}` : ""})
          </TabsTrigger>
          <TabsTrigger value="agents" className="gap-1 sm:gap-2 shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <UserCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">By</span> Agent ({filteredAgentSummary.length}{filterStatus !== "all" ? `/${agentSummary.length}` : ""})
          </TabsTrigger>
          <TabsTrigger value="gp-distribution" className="gap-1 sm:gap-2 shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <PieChart className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">GP Dist</span><span className="sm:hidden">GP</span>
          </TabsTrigger>
          <TabsTrigger value="upload-history" className="gap-1 sm:gap-2 shrink-0 text-xs sm:text-sm px-2 sm:px-3">
            <History className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Upload History</span><span className="sm:hidden">History</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          {showUnmatchedTable ? renderUnmatchedTable() : renderTabContent(filteredAllClientsSummary, true)}
        </TabsContent>

        <TabsContent value="clients" className="mt-4">
          {renderTabContent(filteredClientSummary, false, "clients")}
        </TabsContent>

        <TabsContent value="partners" className="mt-4">
          {renderTabContent(filteredPartnerSummary, false, "partners")}
        </TabsContent>

        <TabsContent value="agents" className="mt-4">
          {renderTabContent(filteredAgentSummary, false, "agents")}
        </TabsContent>

        <TabsContent value="gp-distribution" className="mt-4">
          {renderGpDistribution()}
        </TabsContent>

        {/* Upload History Tab */}
        <TabsContent value="upload-history" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="w-5 h-5 text-indigo-600" />
                    Sales Upload History
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    View all uploaded sales sheets by client, partner, or agent
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={async () => {
                      try {
                        toast.info("Syncing costs from shipment orders...");
                        const response = await fetch(`${BACKEND_URL}/api/sales/sync-costs-from-orders`, {
                          method: "POST",
                          headers: { 
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}` 
                          },
                          body: JSON.stringify({})
                        });
                        const data = await response.json();
                        if (response.ok) {
                          toast.success(`Synced costs for ${data.updated_count} records`);
                          fetchUploadHistory();
                          fetchSales();
                        } else {
                          toast.error(data.detail || "Failed to sync costs");
                        }
                      } catch (error) {
                        toast.error("Failed to sync costs from orders");
                      }
                    }} 
                    className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
                    title="Sync cost_price from shipment orders total_cost"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Sync Costs
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={async () => {
                      try {
                        const response = await fetch(`${BACKEND_URL}/api/sales/recalculate-profits`, {
                          method: "POST",
                          headers: { 
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}` 
                          },
                          body: JSON.stringify({ fix_zero_only: false })
                        });
                        const data = await response.json();
                        if (response.ok) {
                          toast.success(`Fixed profits for ${data.updated_count} records`);
                          fetchUploadHistory();
                          fetchSales();
                        } else {
                          toast.error(data.detail || "Failed to fix profits");
                        }
                      } catch (error) {
                        toast.error("Failed to recalculate profits");
                      }
                    }} 
                    className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                    title="Recalculate profit for all sales (profit = sell - cost)"
                  >
                    <Zap className="w-4 h-4" />
                    Fix Profits
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchUploadHistory} className="gap-2">
                    <RefreshCw className={`w-4 h-4 ${uploadHistoryLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {uploadHistoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
                  <span className="ml-2 text-slate-600">Loading upload history...</span>
                </div>
              ) : uploadHistory.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No upload history found</p>
                  <p className="text-sm mt-1">Upload sales sheets to see history here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 font-medium text-slate-600">Date</th>
                        <th className="text-left p-3 font-medium text-slate-600">Uploaded By</th>
                        <th className="text-right p-3 font-medium text-slate-600">Records</th>
                        <th className="text-right p-3 font-medium text-slate-600">Matched</th>
                        <th className="text-right p-3 font-medium text-slate-600">Unmatched</th>
                        <th className="text-right p-3 font-medium text-slate-600">Total Sell (₹)</th>
                        <th className="text-right p-3 font-medium text-slate-600">Total Cost (₹)</th>
                        <th className="text-right p-3 font-medium text-slate-600">Total Profit (₹)</th>
                        <th className="text-right p-3 font-medium text-slate-600">Clients</th>
                        <th className="text-center p-3 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadHistory.map((upload, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50 transition-colors">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span className="font-medium">{upload.upload_date}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                <Users className="w-4 h-4 text-indigo-600" />
                              </div>
                              <span>{upload.uploaded_by || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="p-3 text-right font-medium">{upload.records_count?.toLocaleString()}</td>
                          <td className="p-3 text-right">
                            <Badge variant="secondary" className="bg-green-100 text-green-700">
                              {upload.matched?.toLocaleString()}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            {upload.unmatched > 0 ? (
                              <Badge variant="secondary" className="bg-red-100 text-red-700">
                                {upload.unmatched?.toLocaleString()}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">0</span>
                            )}
                          </td>
                          <td className="p-3 text-right text-blue-600 font-medium">
                            {formatCurrency(upload.total_sell)}
                          </td>
                          <td className="p-3 text-right text-orange-600 font-medium">
                            {formatCurrency(upload.total_cost)}
                          </td>
                          <td className={`p-3 text-right font-bold ${upload.total_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(upload.total_profit)}
                          </td>
                          <td className="p-3 text-right text-slate-600">{upload.clients_count}</td>
                          <td className="p-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchUploadRawData(upload)}
                              className="gap-1 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                            >
                              <Table className="w-4 h-4" />
                              View Data
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Sales Sheet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-blue-800">Client Upload Format:</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => downloadTemplate('client')}
                  className="gap-1 text-blue-700 border-blue-300 hover:bg-blue-100"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Download Template
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-white rounded p-2 border border-blue-200">
                  <p className="text-xs font-semibold text-blue-800">Required Columns:</p>
                  <ul className="text-xs text-blue-600 mt-1 space-y-0.5">
                    <li>• <strong>Tracking</strong> - AWB/Tracking number</li>
                    <li>• <strong>Total Bill</strong> - Sell price (for GP calculation)</li>
                  </ul>
                </div>
                <div className="bg-white rounded p-2 border border-blue-200">
                  <p className="text-xs font-semibold text-blue-800">Optional Columns:</p>
                  <ul className="text-xs text-blue-600 mt-1 space-y-0.5">
                    <li>• SNo, Date, Code, Name</li>
                    <li>• Medicines, Quantity, Rate</li>
                    <li>• Medicine Total, Shipping</li>
                    <li>• Payment Came, ESR, Narration</li>
                  </ul>
                </div>
              </div>
              <div className="mt-3 p-2 bg-emerald-50 rounded border border-emerald-200">
                <p className="text-xs text-emerald-700">
                  <strong>GP Calculation:</strong> Total Bill - Cost (from Shipment Costing) = Gross Profit
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="replace-sales"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="replace-sales" className="text-sm">
                Replace existing records
              </Label>
            </div>
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="sales-file-upload"
                disabled={uploading}
              />
              <label htmlFor="sales-file-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400" />
                <p className="mt-2 text-sm font-medium">
                  {uploading ? "Uploading..." : "Click to select Excel file"}
                </p>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Raw Partner Sheets Upload Dialog */}
      <Dialog open={showRawPartnerUploadDialog} onOpenChange={setShowRawPartnerUploadDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-600" />
              Upload Partner Sheets (Multi-Sheet Format)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Download Template Buttons */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet className="w-5 h-5 text-amber-600" />
                <p className="font-medium text-amber-800 text-sm">Download Template</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 gap-1 bg-white border-amber-300 text-amber-700 hover:bg-amber-100 text-xs"
                  onClick={() => window.open(`${BACKEND_URL}/api/partner-ledger/template-raw`, '_blank')}
                >
                  <Download className="w-3 h-3" />
                  Raw Format (Familiar)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 gap-1 bg-white border-purple-300 text-purple-700 hover:bg-purple-100 text-xs"
                  onClick={() => window.open(`${BACKEND_URL}/api/partner-ledger/template`, '_blank')}
                >
                  <Download className="w-3 h-3" />
                  Structured Format
                </Button>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm font-medium text-purple-800 mb-2">Smart Raw Data Parser</p>
              <p className="text-xs text-purple-700 mb-3">
                Upload Excel files with multiple sheets where each sheet represents a partner's sales data.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white rounded p-2 border border-purple-200">
                  <p className="text-xs font-semibold text-purple-800">Format Expected:</p>
                  <ul className="text-xs text-purple-600 mt-1 space-y-0.5">
                    <li>• Each <strong>sheet name</strong> = Partner code</li>
                    <li>• Tracking numbers (LP..., RM..., ED...)</li>
                    <li>• Total Bill / Total Cost column</li>
                    <li>• Customer names and products</li>
                  </ul>
                </div>
                <div className="bg-white rounded p-2 border border-purple-200">
                  <p className="text-xs font-semibold text-purple-800">Auto-Detected:</p>
                  <ul className="text-xs text-purple-600 mt-1 space-y-0.5">
                    <li>• Multi-row orders grouped by tracking</li>
                    <li>• Products with quantities</li>
                    <li>• Dates from various formats</li>
                    <li>• GP calculated from shipment costs</li>
                  </ul>
                </div>
              </div>
              <div className="mt-3 p-2 bg-emerald-50 rounded border border-emerald-200">
                <p className="text-xs text-emerald-700">
                  <strong>GP Calculation:</strong> Partner Sell Price - Our Cost = Gross Profit per partner
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="replace-partner-sales"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="replace-partner-sales" className="text-sm">
                Replace existing records for these partners
              </Label>
            </div>
            
            <div className="border-2 border-dashed border-purple-200 rounded-lg p-8 text-center hover:border-purple-400 transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleRawPartnerSheetsUpload}
                className="hidden"
                id="raw-partner-file-upload"
                disabled={uploading}
              />
              <label htmlFor="raw-partner-file-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-purple-400" />
                <p className="mt-2 text-sm font-medium text-purple-700">
                  {uploading ? "Processing sheets..." : "Click to select Excel file with partner sheets"}
                </p>
                <p className="text-xs text-purple-500 mt-1">
                  Supports: CARECRAFT, HEALTH BOX, PHARMA PLUS, etc.
                </p>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calculate Dialog */}
      <Dialog open={showCalculateDialog} onOpenChange={setShowCalculateDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Calculated Sales Preview
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto py-4">
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Records</p>
                <p className="text-lg font-bold">{calculatedSummary.total_records || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-500">Total Sales</p>
                <p className="text-lg font-bold text-blue-600">{formatCurrency(calculatedSummary.total_sales)}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3">
                <p className="text-xs text-orange-500">Total Cost</p>
                <p className="text-lg font-bold text-orange-600">{formatCurrency(calculatedSummary.total_cost)}</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-xs text-emerald-500">Total Profit</p>
                <p className="text-lg font-bold text-emerald-600">{formatCurrency(calculatedSummary.total_profit)}</p>
              </div>
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-2 py-2 text-left">Tracking</th>
                    <th className="px-2 py-2 text-left">Client</th>
                    <th className="px-2 py-2 text-right">Sell</th>
                    <th className="px-2 py-2 text-right">Cost</th>
                    <th className="px-2 py-2 text-right">Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {calculatedSales.slice(0, 100).map((sale, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-2 py-1 font-mono">{sale.tracking}</td>
                      <td className="px-2 py-1">{sale.client_code}</td>
                      <td className="px-2 py-1 text-right text-blue-600">{formatCurrency(sale.sell_price)}</td>
                      <td className="px-2 py-1 text-right text-orange-600">{formatCurrency(sale.cost_price)}</td>
                      <td className={`px-2 py-1 text-right font-medium ${sale.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(sale.profit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {calculatedSales.length > 100 && (
                <div className="text-center py-2 text-xs text-slate-500 bg-slate-50">
                  Showing first 100 of {calculatedSales.length} records
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="pt-4 border-t">
            <div className="flex items-center gap-2 mr-auto">
              <input
                type="checkbox"
                id="replace-calculated"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="replace-calculated" className="text-sm">
                Replace existing records
              </Label>
            </div>
            <Button variant="outline" onClick={() => setShowCalculateDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCalculated} className="bg-emerald-600 hover:bg-emerald-700">
              Save {calculatedSales.length} Records
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Sale Details
            </DialogTitle>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Tracking Number</Label>
                  <p className="font-mono text-sm">{selectedSale.tracking}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Client</Label>
                  <p className="font-medium">{selectedSale.client_code}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Date</Label>
                  <p>{formatDDMMYYDisplay(selectedSale.order_date || selectedSale.sale_date)}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <p>{getStatusBadge(selectedSale.status)}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-500">Sell Price</p>
                    <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedSale.sell_price)}</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3">
                    <p className="text-xs text-orange-500">Cost</p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(selectedSale.cost_price)}</p>
                  </div>
                  <div className={`rounded-lg p-3 ${selectedSale.profit >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <p className={`text-xs ${selectedSale.profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>Profit</p>
                    <p className={`text-lg font-bold ${selectedSale.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(selectedSale.profit)}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-xs text-purple-500">Margin</p>
                    <p className="text-lg font-bold text-purple-600">{selectedSale.profit_margin}%</p>
                  </div>
                </div>
              </div>
              
              {selectedSale.products && selectedSale.products.length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-slate-500">Products</Label>
                  <div className="mt-2 space-y-1">
                    {selectedSale.products.map((p, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-slate-50 px-3 py-1 rounded">
                        <span>{p.name}</span>
                        <span className="text-slate-500">Qty: {p.qty} | Cost: {formatCurrency(p.cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Drill-down Entries Confirmation Dialog */}
      <Dialog open={showDeleteDrilldownDialog} onOpenChange={(open) => { setShowDeleteDrilldownDialog(open); if (!open) { setDeletePassword(""); setEntriesToDelete([]); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Entries
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                Delete <strong>{entriesToDelete.length}</strong> {entriesToDelete.length === 1 ? 'entry' : 'entries'} from <strong>{selectedClient}</strong> Sales view?
              </p>
              <p className="text-xs text-red-600 mt-1">
                Entries will be hidden from Sales & Profit views. Use "Deleted" button to restore later.
              </p>
            </div>
            
            {entriesToDelete.length <= 10 && (
              <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                {entriesToDelete.map((e, i) => (
                  <div key={i} className="flex justify-between bg-slate-50 px-2 py-1 rounded">
                    <span className="font-mono">{e.tracking}</span>
                    <span className="text-blue-600">{formatCurrency(e.sell_price)}</span>
                  </div>
                ))}
              </div>
            )}
            
            <div>
              <Label className="text-xs">Enter Password to Confirm</Label>
              <Input
                type="password"
                value={deletePassword}
                data-testid="delete-drilldown-password"
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter your password"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDrilldownDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              data-testid="confirm-delete-drilldown-btn"
              onClick={handleDeleteDrilldownEntries}
              disabled={!deletePassword || deletingDrilldownEntries}
            >
              {deletingDrilldownEntries ? "Deleting..." : `Delete ${entriesToDelete.length} Entries`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deleted Entries View / Restore Dialog */}
      <Dialog open={showDeletedEntriesDialog} onOpenChange={(open) => { setShowDeletedEntriesDialog(open); if (!open) setRestorePassword(""); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Deleted Entries - {selectedClient}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {deletedEntries.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No deleted entries for this partner</p>
            ) : (
              <>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {deletedEntries.map((entry, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded border">
                      <div>
                        <p className="font-mono text-xs">{entry.tracking}</p>
                        <p className="text-[10px] text-slate-500">
                          Deleted by {entry.deleted_by} on {formatDMY(entry.deleted_at)}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`restore-entry-${idx}`}
                        className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 text-xs h-7"
                        onClick={() => {
                          if (!restorePassword) {
                            toast.error("Enter password first");
                            return;
                          }
                          handleRestoreEntries([entry.tracking]);
                        }}
                      >
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>
                
                <div>
                  <Label className="text-xs">Password (required to restore)</Label>
                  <Input
                    type="password"
                    value={restorePassword}
                    data-testid="restore-password-input"
                    onChange={(e) => setRestorePassword(e.target.value)}
                    placeholder="Enter password to restore"
                    className="mt-1"
                  />
                </div>
                
                <Button
                  className="w-full"
                  variant="outline"
                  data-testid="restore-all-btn"
                  onClick={() => {
                    if (!restorePassword) {
                      toast.error("Enter password first");
                      return;
                    }
                    handleRestoreEntries(deletedEntries.map(e => e.tracking));
                  }}
                  disabled={restoringEntries || !restorePassword}
                >
                  {restoringEntries ? "Restoring..." : `Restore All ${deletedEntries.length} Entries`}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Partner Upload Dialog */}
      <Dialog open={showPartnerUploadDialog} onOpenChange={setShowPartnerUploadDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="w-5 h-5 text-purple-600" />
              Partner Sales Upload
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-purple-800">Partner Upload Format:</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => downloadTemplate('partner')}
                  className="gap-1 text-purple-700 border-purple-300 hover:bg-purple-100"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Download Template
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-white rounded p-2 border border-purple-200">
                  <p className="text-xs font-semibold text-purple-800">Required Columns:</p>
                  <ul className="text-xs text-purple-600 mt-1 space-y-0.5">
                    <li>• <strong>Tracking</strong> - AWB/Tracking number</li>
                    <li>• <strong>Total Bill</strong> - Sell price (for GP calculation)</li>
                  </ul>
                </div>
                <div className="bg-white rounded p-2 border border-purple-200">
                  <p className="text-xs font-semibold text-purple-800">Optional Columns:</p>
                  <ul className="text-xs text-purple-600 mt-1 space-y-0.5">
                    <li>• SNo, Date, Code, Name</li>
                    <li>• Medicines, Quantity, Rate</li>
                    <li>• Medicine Total, Shipping</li>
                    <li>• Payment Came, ESR, Narration</li>
                  </ul>
                </div>
              </div>
              <div className="mt-3 p-2 bg-emerald-50 rounded border border-emerald-200">
                <p className="text-xs text-emerald-700">
                  <strong>GP Calculation:</strong> Total Bill - Cost (from Shipment Costing) = Gross Profit
                </p>
              </div>
            </div>
            
            {/* Partner Selection */}
            <div>
              <Label className="text-sm font-medium">Select Partner</Label>
              <select
                value={selectedPartnerForUpload || ""}
                onChange={(e) => setSelectedPartnerForUpload(e.target.value)}
                className="w-full mt-1 p-2 border rounded-lg text-sm"
              >
                <option value="">-- Select Partner --</option>
                {partnerSummary.map(p => (
                  <option key={p._id} value={p._id}>{p._id}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="replace-partner-sales"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="replace-partner-sales" className="text-sm">
                Replace existing sales for this partner
              </Label>
            </div>
            
            {selectedPartnerForUpload && (
              <div className="border-2 border-dashed border-purple-200 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handlePartnerUpload(e, selectedPartnerForUpload)}
                  className="hidden"
                  id="partner-file-upload"
                  disabled={uploading}
                />
                <label htmlFor="partner-file-upload" className="cursor-pointer">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-purple-400" />
                  <p className="mt-2 text-sm font-medium text-purple-700">
                    {uploading ? "Uploading..." : `Upload for ${selectedPartnerForUpload}`}
                  </p>
                  <p className="text-xs text-purple-400 mt-1">.xlsx or .xls</p>
                </label>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent Bulk Upload Dialog */}
      <Dialog open={showAgentUploadDialog} onOpenChange={setShowAgentUploadDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-orange-600" />
              Agent Bulk Upload
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-orange-800">Agent Upload Format:</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => downloadTemplate('agent')}
                  className="gap-1 text-orange-700 border-orange-300 hover:bg-orange-100"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Download Template
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-white rounded p-2 border border-orange-200">
                  <p className="text-xs font-semibold text-orange-800">Required Columns:</p>
                  <ul className="text-xs text-orange-600 mt-1 space-y-0.5">
                    <li>• <strong>Tracking</strong> - AWB/Tracking number</li>
                    <li>• <strong>Total Bill</strong> - Sell price (for GP calculation)</li>
                  </ul>
                </div>
                <div className="bg-white rounded p-2 border border-orange-200">
                  <p className="text-xs font-semibold text-orange-800">Optional Columns:</p>
                  <ul className="text-xs text-orange-600 mt-1 space-y-0.5">
                    <li>• SNo, Date, Code, Name</li>
                    <li>• Medicines, Quantity, Rate</li>
                    <li>• Medicine Total, Shipping</li>
                    <li>• Payment Came, ESR, Narration</li>
                  </ul>
                </div>
              </div>
              <div className="mt-3 p-2 bg-emerald-50 rounded border border-emerald-200">
                <p className="text-xs text-emerald-700">
                  <strong>GP Calculation:</strong> Total Bill - Cost (from Shipment Costing) = Gross Profit
                </p>
              </div>
            </div>
            
            {/* Agent Selection */}
            <div>
              <Label className="text-sm font-medium">Select Agent</Label>
              <select
                value={selectedAgentForUpload || ""}
                onChange={(e) => setSelectedAgentForUpload(e.target.value)}
                className="w-full mt-1 p-2 border rounded-lg text-sm"
              >
                <option value="">-- Select Agent --</option>
                {agentSummary.map(a => (
                  <option key={a._id} value={a._id}>{a._id}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="replace-agent-sales"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="replace-agent-sales" className="text-sm">
                Replace existing sales uploaded by this agent
              </Label>
            </div>
            
            {selectedAgentForUpload && (
              <div className="border-2 border-dashed border-orange-200 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handleAgentBulkUpload(e, selectedAgentForUpload)}
                  className="hidden"
                  id="agent-file-upload"
                  disabled={uploading}
                />
                <label htmlFor="agent-file-upload" className="cursor-pointer">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-orange-400" />
                  <p className="mt-2 text-sm font-medium text-orange-700">
                    {uploading ? "Uploading..." : `Bulk Upload for ${selectedAgentForUpload}`}
                  </p>
                  <p className="text-xs text-orange-400 mt-1">.xlsx or .xls</p>
                </label>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Ledger Entry Dialog */}
      <Dialog open={showManualEntryDialog} onOpenChange={setShowManualEntryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {manualEntryType === "take" ? (
                <>
                  <DollarSign className="w-5 h-5 text-red-600" />
                  Add Debit Entry (Take)
                </>
              ) : (
                <>
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Add Credit Entry (Give)
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className={`p-3 rounded-lg ${
              manualEntryType === "take" ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"
            }`}>
              <p className={`text-sm ${manualEntryType === "take" ? "text-red-700" : "text-green-700"}`}>
                {manualEntryType === "take" 
                  ? "Recording amount to collect from client" 
                  : "Recording payment received from client"}
              </p>
              <p className="text-xs mt-1 text-slate-500">Client: <strong>{reconciliationSelectedCode}</strong></p>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Amount (₹) *</Label>
              <Input
                type="number"
                placeholder="Enter amount"
                value={manualEntryAmount}
                onChange={(e) => setManualEntryAmount(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Date *</Label>
              <DMYDateInput value={manualEntryDate} onChange={(e) => setManualEntryDate(e.target.value)} className="mt-1" />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Narration/Description *</Label>
              <Input
                placeholder="Enter description"
                value={manualEntryNarration}
                onChange={(e) => setManualEntryNarration(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Tracking Number (Optional)</Label>
              <Input
                placeholder="Enter tracking number if applicable"
                value={manualEntryTracking}
                onChange={(e) => setManualEntryTracking(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowManualEntryDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateManualEntry}
                className={`flex-1 ${
                  manualEntryType === "take" 
                    ? "bg-red-600 hover:bg-red-700" 
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {manualEntryType === "take" ? "Add Debit" : "Add Credit"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Raw Data Dialog */}
      <Dialog open={showUploadRawDataDialog} onOpenChange={setShowUploadRawDataDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-3 border-b">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <Table className="w-5 h-5 text-indigo-600" />
                  Upload Raw Data
                </DialogTitle>
                {selectedUpload && (
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {selectedUpload.upload_date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {selectedUpload.uploaded_by || 'Unknown'}
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {uploadRawDataTotal} records
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openRawDataFullScreen}
                  className="gap-1 text-indigo-600 hover:text-indigo-800"
                  title="Open in Full Screen"
                >
                  <ExternalLink className="w-4 h-4" />
                  Full Screen
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowUploadRawDataDialog(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {/* Search Bar */}
          <div className="py-3 border-b flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by tracking, client, date... (press Enter)"
                value={rawDataSearch}
                onChange={(e) => handleRawDataSearchChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRawDataSearchSubmit()}
                className="pl-9"
              />
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRawDataSearchSubmit}
              disabled={uploadRawDataLoading}
            >
              <Search className="w-4 h-4 mr-1" />
              Search
            </Button>
            {rawDataSearch && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setRawDataSearch('');
                  if (selectedUpload) {
                    fetchUploadRawData(selectedUpload, 1, rawDataSortField, rawDataSortOrder, '');
                  }
                }}
              >
                Clear
              </Button>
            )}
            {rawDataSortField && (
              <span className="text-sm text-slate-500">
                Sorted by: <strong>{rawDataSortField}</strong> ({rawDataSortOrder})
              </span>
            )}
          </div>
          
          <div className="flex-1 overflow-auto">
            {uploadRawDataLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
                <span className="ml-2 text-slate-600">Loading data...</span>
              </div>
            ) : uploadRawData.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No data found for this upload</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-100">
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium text-slate-600">#</th>
                    <th 
                      className="text-left p-2 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleRawDataSort('tracking')}
                    >
                      <div className="flex items-center gap-1">
                        Tracking
                        {rawDataSortField === 'tracking' ? (
                          rawDataSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ChevronsUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </th>
                    <th 
                      className="text-left p-2 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleRawDataSort('client_code')}
                    >
                      <div className="flex items-center gap-1">
                        Client
                        {rawDataSortField === 'client_code' ? (
                          rawDataSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ChevronsUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </th>
                    <th 
                      className="text-left p-2 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleRawDataSort('sale_date')}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        {rawDataSortField === 'sale_date' ? (
                          rawDataSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ChevronsUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </th>
                    <th 
                      className="text-right p-2 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleRawDataSort('sell_price')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Sell (₹)
                        {rawDataSortField === 'sell_price' ? (
                          rawDataSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ChevronsUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </th>
                    <th 
                      className="text-right p-2 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleRawDataSort('cost')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Cost (₹)
                        {rawDataSortField === 'cost' ? (
                          rawDataSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ChevronsUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </th>
                    <th 
                      className="text-right p-2 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleRawDataSort('profit')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Profit (₹)
                        {rawDataSortField === 'profit' ? (
                          rawDataSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ChevronsUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </th>
                    <th 
                      className="text-center p-2 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleRawDataSort('status')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Status
                        {rawDataSortField === 'status' ? (
                          rawDataSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ChevronsUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </th>
                    <th 
                      className="text-left p-2 font-medium text-slate-600 cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleRawDataSort('uploaded_by')}
                    >
                      <div className="flex items-center gap-1">
                        Uploaded By
                        {rawDataSortField === 'uploaded_by' ? (
                          rawDataSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ChevronsUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </th>
                    <th className="text-left p-2 font-medium text-slate-600">
                      <div className="flex items-center gap-1">
                        Narration
                        <span className="text-[10px] text-slate-400">(Currency)</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayRawData.map((row, idx) => {
                    const narration = extractNarration(row);
                    const rowNumber = (rawDataPage - 1) * rawDataPageSize + idx + 1;
                    return (
                    <tr key={row.id || idx} className="border-b hover:bg-slate-50">
                      <td className="p-2 text-slate-400">{rowNumber}</td>
                      <td className="p-2 font-mono text-xs">{row.tracking}</td>
                      <td className="p-2">
                        <Badge variant="outline">{row.client_code || '-'}</Badge>
                      </td>
                      <td className="p-2 text-slate-600">{formatDDMMYYDisplay(row.order_date || row.sale_date)}</td>
                      <td className="p-2 text-right text-blue-600">{formatCurrency(row.sell_price)}</td>
                      <td className="p-2 text-right text-orange-600">{formatCurrency(row.cost_price || row.cost)}</td>
                      <td className={`p-2 text-right font-medium ${(row.profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(row.profit)}
                      </td>
                      <td className="p-2 text-center">
                        {row.status === 'matched' ? (
                          <Badge className="bg-green-100 text-green-700">Matched</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Unmatched</Badge>
                        )}
                      </td>
                      <td className="p-2 text-slate-500">{row.uploaded_by || '-'}</td>
                      <td className="p-2">
                        {narration.hasNarration ? (
                          <div className="flex flex-wrap items-center gap-1 max-w-[250px]">
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                              {narration.currency}
                            </Badge>
                            {narration.narrationParts && narration.narrationParts.length > 0 ? (
                              <span className="text-xs text-slate-600">{narration.narrationParts.join(', ')}</span>
                            ) : (
                              <span className="text-xs text-slate-600">→ {formatCurrency(narration.inrAmount)}</span>
                            )}
                            {narration.hasCrocin && narration.narrationParts?.length > 0 && (
                              <Badge variant="secondary" className="bg-red-100 text-red-700 text-xs">ESR@</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          
          {/* Footer with Pagination */}
          <div className="border-t pt-3 flex items-center justify-between">
            <div className="text-sm text-slate-500">
              Showing {(rawDataPage - 1) * rawDataPageSize + 1}-{Math.min(rawDataPage * rawDataPageSize, uploadRawDataTotal)} of {uploadRawDataTotal} records
            </div>
            
            <div className="flex items-center gap-3">
              {/* Pagination Controls */}
              <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border">
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={rawDataPage === 1 || uploadRawDataLoading}
                  onClick={() => handleRawDataPageChange(1)}
                  title="First Page"
                  className="px-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <ChevronLeft className="w-4 h-4 -ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={rawDataPage === 1 || uploadRawDataLoading}
                  onClick={() => handleRawDataPageChange(rawDataPage - 1)}
                  className="gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </Button>
                <span className="px-4 py-1.5 text-sm font-medium bg-white border rounded-md shadow-sm">
                  Page {rawDataPage} of {Math.ceil(uploadRawDataTotal / rawDataPageSize)}
                </span>
                <Button 
                  variant="default" 
                  size="sm"
                  disabled={rawDataPage >= Math.ceil(uploadRawDataTotal / rawDataPageSize) || uploadRawDataLoading}
                  onClick={() => handleRawDataPageChange(rawDataPage + 1)}
                  className="gap-1 bg-indigo-600 hover:bg-indigo-700"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={rawDataPage >= Math.ceil(uploadRawDataTotal / rawDataPageSize) || uploadRawDataLoading}
                  onClick={() => handleRawDataPageChange(Math.ceil(uploadRawDataTotal / rawDataPageSize))}
                  title="Last Page"
                  className="px-2"
                >
                  <ChevronRight className="w-4 h-4" />
                  <ChevronRight className="w-4 h-4 -ml-2" />
                </Button>
              </div>
              
              <Button variant="outline" onClick={() => setShowUploadRawDataDialog(false)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* DP Client Sales Generation Result Dialog */}
      <Dialog open={showDpSalesResultDialog} onOpenChange={setShowDpSalesResultDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-green-600" />
              DP Client Sales Generation Result
            </DialogTitle>
          </DialogHeader>
          
          {dpSalesResult && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-700">{dpSalesResult.generated || dpSalesResult.generated_new_records || 0}</p>
                    <p className="text-sm text-green-600">Records Generated</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-700">{dpSalesResult.skipped_existing || 0}</p>
                    <p className="text-sm text-blue-600">Already Existed</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-amber-700">{dpSalesResult.error_count || dpSalesResult.errors?.length || 0}</p>
                    <p className="text-sm text-amber-600">Errors/Missing Prices</p>
                  </CardContent>
                </Card>
              </div>

              {/* DP Clients Processed */}
              {dpSalesResult.dp_clients_processed?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-700">DP Clients Processed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {dpSalesResult.dp_clients_processed.map(client => (
                        <Badge key={client} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {client}
                          {dpSalesResult.sales_by_client && (
                            <span className="ml-1 text-green-600">({dpSalesResult.sales_by_client[client] || 0})</span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Errors */}
              {dpSalesResult.errors?.length > 0 && (
                <Card className="border-amber-200">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-sm font-medium text-amber-700 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Missing Price Errors ({dpSalesResult.errors.length})
                        </CardTitle>
                        <p className="text-xs text-slate-500 mt-1">
                          These products need to be added to the DP Client's price list
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={handleExportMissingProducts}
                          className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50 h-7 text-xs"
                          data-testid="export-missing-products-btn"
                        >
                          <Download className="w-3 h-3" />
                          Export
                        </Button>
                        <Button 
                          size="sm"
                          onClick={handlePrepareBulkImport}
                          className="gap-1 bg-green-600 hover:bg-green-700 h-7 text-xs"
                          data-testid="import-with-prices-btn"
                        >
                          <Upload className="w-3 h-3" />
                          Import with Prices
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-60 overflow-auto space-y-1">
                      {dpSalesResult.errors.slice(0, 50).map((err, idx) => (
                        <div key={idx} className="text-xs p-2 bg-amber-50 rounded flex items-start gap-2">
                          <AlertCircle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="font-medium">{err.client_code}</span>
                            <span className="text-slate-500 mx-1">|</span>
                            <span className="text-slate-600">{err.tracking}</span>
                            <span className="text-slate-500 mx-1">|</span>
                            <span className="text-amber-700">{err.product_name}</span>
                          </div>
                        </div>
                      ))}
                      {dpSalesResult.errors.length > 50 && (
                        <p className="text-xs text-slate-500 pt-2">
                          ... and {dpSalesResult.errors.length - 50} more errors
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Message */}
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">
                {dpSalesResult.message}
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowDpSalesResultDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog for DP Client Products */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              Import Missing Products with Prices
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Enter sell prices for the missing products. Only products with prices will be imported.
            </p>
          </DialogHeader>
          
          {bulkImportData.length > 0 && (
            <div className="space-y-4">
              {/* Group by client */}
              {Object.entries(
                bulkImportData.reduce((acc, item, idx) => {
                  const cc = item.client_code;
                  if (!acc[cc]) acc[cc] = [];
                  acc[cc].push({ ...item, idx });
                  return acc;
                }, {})
              ).map(([clientCode, products]) => (
                <Card key={clientCode} className="border-slate-200">
                  <CardHeader className="py-2 px-3 bg-slate-50">
                    <CardTitle className="text-sm font-medium flex items-center justify-between">
                      <span className="text-green-700">{clientCode}</span>
                      <Badge variant="outline" className="text-xs">{products.length} products</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-auto">
                      {products.map((p) => (
                        <div key={p.idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded border">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate" title={p.product_name}>
                              {p.product_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-xs text-slate-500">₹</span>
                            <Input 
                              type="number"
                              step="0.01"
                              placeholder="Price"
                              className="w-20 h-7 text-xs"
                              value={p.sell_price}
                              onChange={(e) => updateBulkImportPrice(p.idx, e.target.value)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Summary */}
              <div className="flex items-center justify-between bg-slate-50 p-3 rounded">
                <div className="text-sm text-slate-600">
                  <span className="font-medium text-green-700">
                    {bulkImportData.filter(p => p.sell_price && !isNaN(parseFloat(p.sell_price))).length}
                  </span> of {bulkImportData.length} products have prices
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowBulkImportDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleBulkImport}
                    disabled={importingProducts || bulkImportData.filter(p => p.sell_price).length === 0}
                    className="bg-green-600 hover:bg-green-700 gap-2"
                  >
                    {importingProducts ? (
                      <><RefreshCw className="w-4 h-4 animate-spin" /> Importing...</>
                    ) : (
                      <><Upload className="w-4 h-4" /> Import & Generate Sales</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesPage;
